import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiPlus, 
  FiTrash2, 
  FiEdit3, 
  FiSave, 
  FiEye,
  FiCopy,
  FiSettings,
  FiType,
  FiMail,
  FiCalendar,
  FiCheckSquare,
  FiSquare,
  FiList,
  FiAlignLeft,
  FiMinus,
  FiX,
  FiHash,
  FiShare2,
  FiCpu,
  FiEyeOff,
  FiUser,
  FiStar,
  FiUpload,
  FiHome,
  FiArrowLeft,
  FiChevronRight,
  FiChevronDown,
  FiUsers,
  FiRefreshCw,
  FiMapPin,
} from 'react-icons/fi';
import { formsAPI, foldersAPI } from '../services/api';
import ImageUpload from './ImageUpload';
import ShareModal from './ShareModal';
import ComputationExamples from './ComputationExamples';
import UserShareModal from './UserShareModal';
import MapLocationField from './MapLocationField';

const BuilderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  background: white;
  color: black;
  min-height: 100vh;
  overflow: visible;
`;

const FixedProgressWrapper = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  width: 100vw;
  height: auto;
  z-index: 1000;
  display: flex;
  justify-content: center;
  padding: 0 20px;
  pointer-events: none;
  background: transparent;
  isolation: isolate;
  will-change: transform;
`;

const FixedProgressContainer = styled.div`
  background: white;
  border-radius: 0;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  width: 100%;
  max-width: none;
  pointer-events: auto;
  position: relative;
  z-index: 1001;
  margin-top: 0;
  transform: translateY(0);
  isolation: isolate;
`;

const ProgressContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  max-width: 1600px;
  z-index: 100;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ProgressTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: black;
  margin: 0;
  letter-spacing: -0.5px;
`;

const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 0;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
`;

const Step = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'completed' && prop !== 'active',
})`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.3s ease;
  position: relative;
  flex: 0 0 auto;
  justify-content: center;
  
  ${props => {
    if (props.active) {
      return `
        background: black;
        color: white;
      `;
    } else if (props.completed) {
      return `
        background: #f3f4f6;
        color: #111827;
        border: 1px solid #e5e7eb;
      `;
    } else {
      return `
        background: #ffffff;
        color: #374151;
        border: 1px solid #e5e7eb;
      `;
    }
  }}
`;


const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  background: white;
  margin-top: 80px; /* increase spacing under fixed header */
  padding: 20px 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
    margin-top: 70px;
  }
`;

const MainPanel = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  position: relative;
  padding: 20px;
`;


const PanelHeader = styled.div`
  padding: 32px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const PanelTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: black;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
`;

const PanelSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const GuidanceCard = styled.div`
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 20px;
  }
`;

const GuidanceTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 8px 0;
`;

const GuidanceText = styled.p`
  font-size: 14px;
  color: #3730a3;
  margin: 0;
  line-height: 1.5;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%);
  color: black;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.6s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.15) 100%);
    border-color: rgba(0, 0, 0, 0.4);
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
    transition: all 0.1s ease;
  }
`;

const FieldQuickActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding: 16px;
  background: rgba(249, 250, 251, 0.8);
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  justify-content: center;
`;

const QuickActionButtonSmall = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
  color: black;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%);
    border-color: rgba(0, 0, 0, 0.4);
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
    transition: all 0.1s ease;
  }
`;

const FieldSelectorOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const FieldSelectorModal = styled.div`
  position: fixed;
  top: ${props => props.position.top}px;
  left: ${props => props.position.left}px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 24px;
  width: 900px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  overflow: auto;
  z-index: 1001;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const FieldSelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
`;

const FieldSelectorTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: black;
  margin: 0;
`;

const FieldSelectorClose = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const FieldSelectorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 3px;
  }
`;

const FieldSelectorOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
  color: #374151;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;

  &:hover {
    border-color: rgba(0, 0, 0, 0.3);
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
    transition: all 0.1s ease;
  }
`;

const FieldSelectorIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
`;

const FieldSelectorLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
`;

const FieldSelectorDescription = styled.span`
  font-size: 11px;
  color: #6b7280;
  line-height: 1.3;
`;

const FormPreview = styled.div`
  padding: 32px;
  min-height: 500px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
  background: white;
  border-radius: 20px;
  border: 2px dashed #d1d5db;
  position: relative;
  overflow: hidden;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.7;
  color: #6b7280;
  animation: float 3s ease-in-out infinite;
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
`;

const EmptyStateDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.6;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const EmptyStateActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const FormTitle = styled.input`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0;
  border: 2px solid transparent;
  outline: none;
  width: 100%;
  background: transparent;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:focus {
    background: #f9fafb;
    border-color: black;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 500;
  }
`;

const FormDescription = styled.textarea`
  width: 100%;
  border: 2px solid transparent;
  outline: none;
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 0;
  background: transparent;
  resize: vertical;
  min-height: 80px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:focus {
    background: #f9fafb;
    border-color: black;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FieldList = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isDragOver'].includes(prop),
})`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 12px;
  align-items: start;
`;

const FieldItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isDragging', 'fieldWidth'].includes(prop),
})`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px;
  background: white;
  transition: all 0.2s ease;
  
  /* Grid column span based on fieldWidth */
  grid-column: ${props => {
    if (props.fieldWidth === 'full') return 'span 12';
    if (props.fieldWidth === 'half') return 'span 6';
    if (props.fieldWidth === 'third') return 'span 4';
    if (props.fieldWidth === 'two-thirds') return 'span 8';
    if (props.fieldWidth === 'quarter') return 'span 3';
    return 'span 12'; // default to full width
  }};

  &:hover {
    border-color: black;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  ${props => props.isDragging && `
    border-color: black;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  `}
  
  /* Force full width for section, heading, and subheading */
  ${props => (props.fieldType === 'section' || props.fieldType === 'heading' || props.fieldType === 'subheading') && `
    grid-column: span 12 !important;
  `}
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 8px;
`;

const FieldLabelInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  font-weight: 500;
  color: #1f2937;
  background: transparent;

  &:focus {
    background: #f9fafb;
    border-radius: 4px;
    padding: 4px 8px;
  }
`;

const FieldActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 16px;
`;

const FieldActionButton = styled.button`
  padding: 4px;
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const FieldInput = styled.div`
  margin-bottom: 8px;
  overflow: visible;
  max-height: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #f9fafb;
  color: #6b7280;

  &:focus {
    outline: none;
    border-color: black;
    background: white;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #f9fafb;
  color: #6b7280;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: black;
    background: white;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #f9fafb;
  color: #6b7280;

  &:focus {
    outline: none;
    border-color: black;
    background: white;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  margin: 0;
`;

const HeadingElement = styled.h3.withConfig({
  shouldForwardProp: (prop) => prop !== 'level',
})`
  font-size: ${props => {
    switch (props.level) {
      case 1: return '28px';
      case 2: return '24px';
      case 3: return '20px';
      case 4: return '18px';
      case 5: return '16px';
      case 6: return '14px';
      default: return '20px';
    }
  }};
  font-weight: 600;
  color: #1f2937;
  margin: 24px 0 16px 0;
  padding: 0;
  border: none;
  background: none;
  width: 100%;
  outline: none;
  
  &:focus {
    border-bottom: 2px solid black;
  }
`;

const HeadingInput = styled.input`
  font-size: ${props => {
    switch (props.level) {
      case 1: return '28px';
      case 2: return '24px';
      case 3: return '20px';
      case 4: return '18px';
      case 5: return '16px';
      case 6: return '14px';
      default: return '20px';
    }
  }};
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  padding: 8px 0;
  border: none;
  background: transparent;
  width: 100%;
  outline: none;
  border-bottom: 2px solid transparent;

  &:focus {
    border-bottom-color: black;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Radio = styled.input`
  margin: 0;
`;

const OptionsInput = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: #f9fafb;
  color: #6b7280;
  min-height: 60px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: black;
    background: white;
  }
`;

const OptionsContainer = styled.div`
  margin-top: 12px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const OptionInput = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;

  &:focus {
    outline: none;
    border-color: black;
  }
`;

const OptionButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AddOptionButton = styled(OptionButton)`
  background: #10b981;
  color: white;

  &:hover {
    background: #059669;
  }
`;

const RemoveOptionButton = styled(OptionButton)`
  background: #ef4444;
  color: white;

  &:hover {
    background: #dc2626;
  }
`;

const FieldSettingsContainer = styled.div`
  margin-top: 6px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
`;

const SettingGroup = styled.div`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
`;

const SettingInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;

  &:focus {
    outline: none;
    border-color: black;
  }
`;











const FieldIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const FieldInfo = styled.div`
  flex: 1;
  text-align: left;
`;

const FieldName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
  letter-spacing: -0.02em;
`;

const FieldDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const PrimaryButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: black;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6b7280;
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  
  ${props => props.required && `
    &::after {
      content: ' *';
      color: #ef4444;
    }
  `}
`;

const fieldTypes = [
  { 
    type: 'text', 
    label: 'Text', 
    icon: FiType,
    description: 'Perfect for names, titles, or short responses',
    category: 'basic'
  },
  { 
    type: 'email', 
    label: 'Email', 
    icon: FiMail,
    description: 'Collects email addresses automatically',
    category: 'basic'
  },
  { 
    type: 'number', 
    label: 'Number', 
    icon: FiHash,
    description: 'For quantities, ages, or any numbers',
    category: 'basic'
  },
  { 
    type: 'date', 
    label: 'Date', 
    icon: FiCalendar,
    description: 'Easy date selection with calendar',
    category: 'basic'
  },
  { 
    type: 'textarea', 
    label: 'Text Area', 
    icon: FiAlignLeft,
    description: 'For detailed comments or long responses',
    category: 'basic'
  },
  { 
    type: 'select', 
    label: 'Dropdown', 
    icon: FiList,
    description: 'Let users pick from your list',
    category: 'choice'
  },
  { 
    type: 'checkbox', 
    label: 'Checkbox', 
    icon: FiCheckSquare,
    description: 'Allow multiple selections from a list',
    category: 'choice'
  },
  { 
    type: 'radio', 
    label: 'Radio', 
    icon: FiSquare,
    description: 'Single choice from multiple options',
    category: 'choice'
  },
  { 
    type: 'calculation', 
    label: 'Calculate', 
    icon: FiCpu,
    description: 'Automatically calculates totals or formulas',
    category: 'advanced'
  },
  { 
    type: 'rating', 
    label: 'Rating', 
    icon: FiStar,
    description: 'Let users rate with 1-5 stars',
    category: 'advanced'
  },
  { 
    type: 'file', 
    label: 'File Upload', 
    icon: FiUpload,
    description: 'Allow users to upload documents, images, or files',
    category: 'advanced'
  },
  { 
    type: 'contact', 
    label: 'Contact Information', 
    icon: FiUser,
    description: 'Ready-made contact details collection',
    category: 'blocks'
  },
  { 
    type: 'table', 
    label: 'Table Form', 
    icon: FiList,
    description: 'Create a structured table with rows and columns for data entry',
    category: 'blocks'
  },
  {
    type: 'matrix',
    label: 'Matrix',
    icon: FiList,
    description: 'Grid with dynamic rows/columns; cells as radio/checkbox/input',
    category: 'blocks'
  },
  {
    type: 'section',
    label: 'Section Header',
    icon: FiType,
    description: 'Add a heading and optional subheading/divider before a section',
    category: 'blocks'
  },
  {
    type: 'subheading',
    label: 'Subheading',
    icon: FiType,
    description: 'Add a subheading that appears within grouped sections',
    category: 'blocks'
  },
  {
    type: 'slider',
    label: 'Slider',
    icon: FiHash,
    description: 'Drag to select a value in a range',
    category: 'basic'
  },
  {
    type: 'mapLocation',
    label: 'Current location',
    icon: FiMapPin,
    description: 'Respondents tap a button to capture their device’s current GPS position; latitude and longitude are saved',
    category: 'advanced'
  },
  {
    type: 'repeatable',
    label: 'Repeatable Group',
    icon: FiRefreshCw,
    description: 'Create a group of fields that can be repeated multiple times (e.g., dish tasting with ratings)',
    category: 'blocks'
  },
  {
    type: 'content',
    label: 'Plain Text / Content',
    icon: FiAlignLeft,
    description: 'Add informational text, instructions, or any static content to your form',
    category: 'blocks'
  },
];

const fieldCategories = {
  basic: { label: 'Common Questions', icon: '📝' },
  choice: { label: 'Multiple Choice', icon: '☑️' },
  advanced: { label: 'Special Features', icon: '⚙️' },
  blocks: { label: 'Quick Blocks', icon: '🧩' }
};

const steps = [
  { id: 1, title: 'Form Details', description: 'Give your form a name and description' },
  { id: 2, title: 'Add Questions', description: 'Create questions for your form' },
  { id: 3, title: 'Preview & Test', description: 'See how your form looks' },
  { id: 4, title: 'Save & Share', description: 'Make your form live' }
];

function FormBuilder({ formId }) {
  console.log('FormBuilder component loaded with formId:', formId);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: 'Click to Add Form Title',
    description: '',
    submit_button_text: '',
    form_structure: [],
    enableMultiStep: false,
    groupSectionsWithQuestions: false,
    labelFontSize: '14px',
    dailyQuotaLimit: null,
    submissionStartDate: null,
    submissionEndDate: null
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [shareModal, setShareModal] = useState({ isOpen: false, form: null });
  const [userShareModal, setUserShareModal] = useState({ isOpen: false, item: null });
  const [showComputationExamples, setShowComputationExamples] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  console.log('FormBuilder currentStep initialized to:', currentStep);
  const [showGuidance, setShowGuidance] = useState(true);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [fieldSelectorPosition, setFieldSelectorPosition] = useState({ top: 0, left: 0 });
  const [insertPosition, setInsertPosition] = useState(null);
  const [changeFieldTypeId, setChangeFieldTypeId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFolderId, setSaveFolderId] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', ''
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [oneQuestionMode, setOneQuestionMode] = useState(false);
  const [focusedFieldIndex, setFocusedFieldIndex] = useState(0);
  
  // State for toggling conditional/nested fields sections per field and option
  const [expandedSections, setExpandedSections] = useState({});
  
  const toggleSection = (fieldId, sectionType, optionIndex = null) => {
    const key = optionIndex !== null 
      ? `${fieldId}_${sectionType}_${optionIndex}` 
      : `${fieldId}_${sectionType}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  const isSectionExpanded = (fieldId, sectionType, optionIndex = null) => {
    const key = optionIndex !== null 
      ? `${fieldId}_${sectionType}_${optionIndex}` 
      : `${fieldId}_${sectionType}`;
    return expandedSections[key] || false;
  };
  
  // Refs for autosave with creation lock pattern
  const currentFormIdRef = React.useRef(formId);
  const creationInProgressRef = React.useRef(false);
  const lastAutoSaveRef = React.useRef(null);
  const autoSaveTimeoutRef = React.useRef(null);

  // Update ref when formId prop changes
  useEffect(() => {
    currentFormIdRef.current = formId;
  }, [formId]);

  // Lock body scroll when field selector modal is open
  useEffect(() => {
    if (showFieldSelector) {
      // Store original overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      // Lock scroll
      document.body.style.overflow = 'hidden';
      
      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [showFieldSelector]);

  // Sync step with header controls via CustomEvent
  useEffect(() => {
    const onStepRequest = (e) => {
      if (e && e.detail && typeof e.detail.stepId === 'number') {
        setCurrentStep(e.detail.stepId);
      }
    };
    window.addEventListener('builder:step:request', onStepRequest);
    return () => window.removeEventListener('builder:step:request', onStepRequest);
  }, []);

  useEffect(() => {
    const event = new CustomEvent('builder:step:update', { detail: { stepId: currentStep } });
    window.dispatchEvent(event);
  }, [currentStep]);

  // Keyboard shortcut: Ctrl/Cmd + K to open field selector (quick add)
  useEffect(() => {
    const onKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        // Ensure we are on the Add Questions step for clarity
        setCurrentStep(2);
        // Center field selector
        setFieldSelectorPosition({ top: Math.max(20, (window.innerHeight - 500) / 2), left: Math.max(20, (window.innerWidth - 900) / 2) });
        setShowFieldSelector(true);
        setShowQuickAdd(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Check for template or AI-generated data on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromTemplate = urlParams.get('fromTemplate');
    const fromAI = urlParams.get('fromAI');

    if (fromTemplate) {
      const templateData = localStorage.getItem('selectedTemplate');
      if (templateData) {
        try {
          const template = JSON.parse(templateData);
          setForm({
            title: template.title || 'Click to Add Form Title',
            description: template.description || '',
            form_structure: template.form_structure || [],
            logo_url: template.logo_url || ''
          });
          toast.success('Template loaded successfully!');
          // Clear the template data from localStorage
          localStorage.removeItem('selectedTemplate');
          // Remove the URL parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error parsing template data:', error);
          toast.error('Failed to load template data');
        }
      }
    } else if (fromAI) {
      const aiData = localStorage.getItem('aiGeneratedForm');
      if (aiData) {
        try {
          const aiForm = JSON.parse(aiData);
          console.log('AI Form loaded from localStorage:', aiForm);
          console.log('AI Form structure length:', aiForm.form_structure ? aiForm.form_structure.length : 'undefined');
          console.log('AI Form structure:', aiForm.form_structure);
          
          setForm({
            title: aiForm.title || 'Click to Add Form Title',
            description: aiForm.description || '',
            form_structure: aiForm.form_structure || [],
            logo_url: aiForm.logo_url || ''
          });
          
          console.log('Form state set with structure length:', aiForm.form_structure ? aiForm.form_structure.length : 0);
          toast.success('AI-generated form loaded successfully!');
          // Clear the AI data from localStorage
          localStorage.removeItem('aiGeneratedForm');
          // Remove the URL parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error parsing AI-generated data:', error);
          toast.error('Failed to load AI-generated form data');
        }
      } else {
        console.log('No AI data found in localStorage');
      }
    }
  }, []);

  const getCurrentStepData = () => {
    return steps.find(step => step.id === currentStep) || steps[0];
  };

  const updateStep = (stepId) => {
    setCurrentStep(stepId);
  };

  const isStepCompleted = (stepId) => {
    switch(stepId) {
      case 1: return form.title && form.description;
      case 2: return form.form_structure && form.form_structure.length > 0;
      case 3: return form.form_structure && form.form_structure.length > 0;
      case 4: return false; // Always false until actually published
      default: return false;
    }
  };

  useEffect(() => {
    if (formId) {
      loadForm();
    }
    loadFolders();
  }, [formId]);

  // Auto-save effect with debounce and creation lock
  useEffect(() => {
    // Skip auto-save on initial mount or if form is still default/empty
    if (form.title === 'Click to Add Form Title' && form.form_structure.length === 0) {
      console.log('Skipping autosave - form is empty or default');
      return;
    }
    
    console.log('Form data changed, setting autosave timeout');
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set new timeout (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      // Prevent simultaneous saves
      if (saving || loading) {
        console.log('Skipping autosave - already saving or loading');
        return;
      }
      
      // Time-based debounce check
      const now = Date.now();
      if (lastAutoSaveRef.current && (now - lastAutoSaveRef.current) < 3000) {
        console.log('Skipping autosave - too soon after last save');
        return;
      }
      
      try {
        setAutoSaveStatus('saving');
        lastAutoSaveRef.current = now;
        
        const payload = {
          title: form.title,
          description: form.description,
          submit_button_text: (form.submit_button_text && String(form.submit_button_text).trim()) || null,
          form_structure: form.form_structure,
          logo_url: form.logo_url,
          folder_id: selectedFolderId,
          // Convert to boolean, handling both boolean and numeric (1/0) values from SQL Server
          enable_multi_step: form.enableMultiStep === true || form.enableMultiStep === 1 || form.enableMultiStep === '1' || form.enableMultiStep === 'true',
          group_sections_with_questions: form.groupSectionsWithQuestions === true || form.groupSectionsWithQuestions === 1 || form.groupSectionsWithQuestions === '1' || form.groupSectionsWithQuestions === 'true',
          label_font_size: form.labelFontSize || '14px',
          daily_quota_limit: form.dailyQuotaLimit && form.dailyQuotaLimit > 0 ? parseInt(form.dailyQuotaLimit) : null,
          submission_start_date: form.submissionStartDate || null,
          submission_end_date: form.submissionEndDate || null
        };
        
        console.log('Autosave payload:', payload);
        console.log('enable_multi_step value:', payload.enable_multi_step, 'type:', typeof payload.enable_multi_step);
        console.log('Form state before autosave:', {
          enableMultiStep: form.enableMultiStep,
          enableMultiStepType: typeof form.enableMultiStep,
          groupSectionsWithQuestions: form.groupSectionsWithQuestions,
          groupSectionsWithQuestionsType: typeof form.groupSectionsWithQuestions
        });
        
        // CRITICAL: Check if we have a form ID using ref (immediate, no render delay)
        const existingFormId = currentFormIdRef.current;
        
        if (existingFormId) {
          // UPDATE EXISTING FORM
          console.log('Autosave - updating existing form:', existingFormId);
          await formsAPI.update(existingFormId, payload);
          console.log('Successfully updated form');
        } else {
          // CREATE NEW FORM (with creation lock)
          
          // Check creation lock
          if (creationInProgressRef.current) {
            console.log('Skipping autosave - creation already in progress');
            setAutoSaveStatus('');
            return;
          }
          
          // Set creation lock
          console.log('Autosave - creating new form (setting lock)');
          creationInProgressRef.current = true;
          
          try {
            // Create form
            const response = await formsAPI.create(payload);
            const newFormId = response.data.data.id;
            console.log('Successfully created form:', newFormId);
            
            // Store form ID in ref (immediate access for next autosave)
            currentFormIdRef.current = newFormId;
            
            // Update URL without reload
            window.history.replaceState(null, '', `/builder/${newFormId}`);
            
            console.log('Form ID stored, future autosaves will UPDATE this form');
            toast.success('Form created successfully', { autoClose: 1500 });
          } finally {
            // Release creation lock
            creationInProgressRef.current = false;
            console.log('Creation lock released');
          }
        }
        
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      } catch (error) {
        console.error('Autosave error:', error);
        setAutoSaveStatus('');
        toast.error(`Autosave failed: ${error.response?.data?.error || error.message}`);
        
        // Release lock on error
        creationInProgressRef.current = false;
      }
    }, 2000); // 2 second debounce

    // Cleanup
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [form.title, form.description, form.submit_button_text, form.form_structure, form.logo_url, form.enableMultiStep, form.groupSectionsWithQuestions, form.labelFontSize, selectedFolderId, loading, saving]);

  const loadFolders = async () => {
    try {
      const response = await foldersAPI.getFolders();
      setFolders(response.data.data || []);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };
  
  // Check if user has permission to create/edit forms
  const hasCreatePermission = user?.permissions?.includes('form.create');
  const hasUpdatePermission = user?.permissions?.includes('form.update');
  
  if (!user || (!hasCreatePermission && !hasUpdatePermission)) {
    return (
      <BuilderContainer>
        <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to create or edit forms.</p>
        </div>
      </BuilderContainer>
    );
  }

  const loadForm = async () => {
    try {
      setLoading(true);
      const response = await formsAPI.getById(formId);
      const formData = response.data.data;
      
      // Parse form_structure if it's a JSON string
      if (typeof formData.form_structure === 'string') {
        try {
          formData.form_structure = JSON.parse(formData.form_structure);
        } catch (e) {
          console.error('Failed to parse form_structure:', e);
          formData.form_structure = [];
        }
      }
      
      // Ensure form_structure is an array
      if (!Array.isArray(formData.form_structure)) {
        console.warn('form_structure is not an array, converting:', formData.form_structure);
        formData.form_structure = [];
      }

      if (formData.id != null) {
        formData.id = String(formData.id).replace(/[{}]/g, '');
      } else if (formId) {
        formData.id = String(formId).replace(/[{}]/g, '');
      }
      
      // Debug: Log repeatable fields
      formData.form_structure.forEach((field, idx) => {
        if (field.type === 'repeatable') {
          console.log(`Loaded repeatable field ${idx}:`, {
            id: field.id,
            label: field.label,
            repeatableFieldsCount: field.repeatableFields?.length || 0,
            repeatableFields: field.repeatableFields
          });
        }
      });
      
      // Normalize enable_multi_step to enableMultiStep for frontend
      // SQL Server returns Bit type as 1/0, convert to boolean
      const enableMultiStepValue = formData.enable_multi_step !== undefined ? formData.enable_multi_step : false;
      formData.enableMultiStep = enableMultiStepValue === true || enableMultiStepValue === 1 || enableMultiStepValue === '1' || enableMultiStepValue === 'true';
        console.log('Loaded enable_multi_step:', formData.enable_multi_step, '-> enableMultiStep:', formData.enableMultiStep);
      
      // Normalize group_sections_with_questions to groupSectionsWithQuestions for frontend
      // SQL Server returns Bit type as 1/0, convert to boolean
      const groupSectionsValue = formData.group_sections_with_questions !== undefined ? formData.group_sections_with_questions : false;
      formData.groupSectionsWithQuestions = groupSectionsValue === true || groupSectionsValue === 1 || groupSectionsValue === '1' || groupSectionsValue === 'true';
        console.log('Loaded group_sections_with_questions:', formData.group_sections_with_questions, '-> groupSectionsWithQuestions:', formData.groupSectionsWithQuestions);
      
      // Normalize label_font_size to labelFontSize for frontend
      if (formData.label_font_size !== undefined) {
        formData.labelFontSize = formData.label_font_size;
      }
      
      // Normalize daily_quota_limit to dailyQuotaLimit for frontend
      if (formData.daily_quota_limit !== undefined && formData.daily_quota_limit !== null) {
        formData.dailyQuotaLimit = formData.daily_quota_limit;
      } else {
        formData.dailyQuotaLimit = null;
      }
      
      // Normalize submission_start_date and submission_end_date for frontend
      if (formData.submission_start_date !== undefined && formData.submission_start_date !== null) {
        formData.submissionStartDate = formData.submission_start_date;
      } else {
        formData.submissionStartDate = null;
      }
      if (formData.submission_end_date !== undefined && formData.submission_end_date !== null) {
        formData.submissionEndDate = formData.submission_end_date;
      } else {
        formData.submissionEndDate = null;
      }
      
      setForm(formData);
      setSelectedFolderId(formData.folder_id || null);
    } catch (error) {
      console.error('Load form error:', error);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const addField = (fieldType, positionOverride = null) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: fieldType,
      label: fieldType === 'heading'
        ? ''
        : fieldType === 'section'
        ? 'Section Title'
        : fieldType === 'repeatable'
        ? 'Repeatable Group'
        : fieldType === 'subheading'
        ? 'Subheading'
        : fieldType === 'content'
        ? 'Enter your content here...'
        : fieldType === 'mapLocation'
        ? 'Current location'
        : 'Click to add a question',
      name: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      required: false,
      fieldWidth: 'full', // Default to full width: 'full', 'half', 'third', 'two-thirds', 'quarter'
    level: fieldType === 'heading' ? 2 : fieldType === 'section' ? 2 : fieldType === 'subheading' ? 4 : undefined, // Default to H2 for headings/sections, H4 for subheadings
      options: fieldType === 'select' || fieldType === 'radio' || fieldType === 'checkbox' ? ['Option 1', 'Option 2'] : [],
      allowComment: fieldType === 'select' || fieldType === 'radio' || fieldType === 'checkbox' ? false : undefined, // Allow additional comments
      commentLabel: fieldType === 'select' || fieldType === 'radio' || fieldType === 'checkbox' ? 'Additional Comments' : undefined, // Comment field label
      checkboxLayout: fieldType === 'checkbox' ? 'vertical' : undefined, // Checkbox display layout: 'vertical' (line by line) or 'horizontal' (in a row)
      formula: fieldType === 'calculation' ? '' : undefined, // Formula for calculations
      decimalPlaces: fieldType === 'calculation' ? 2 : undefined, // Decimal places for calculations
      currency: fieldType === 'calculation' ? false : undefined, // Currency formatting
      maxRating: fieldType === 'rating' ? 5 : undefined, // Maximum rating value
      ratingLabels: fieldType === 'rating' ? ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] : undefined, // Rating labels
      ratingIcon: fieldType === 'rating' ? 'star' : undefined, // Rating icon type
      acceptedFileTypes: fieldType === 'file' ? ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'] : undefined, // Accepted file types
      maxFileSize: fieldType === 'file' ? 10 : undefined, // Maximum file size in MB
      multipleFiles: fieldType === 'file' ? false : undefined, // Allow multiple file uploads
      // Slider defaults
      min: fieldType === 'slider' ? 0 : undefined,
      max: fieldType === 'slider' ? 10 : undefined,
      step: fieldType === 'slider' ? 1 : undefined,
    // Matrix defaults
    matrixRows: fieldType === 'matrix' ? [] : undefined, // Start empty, use count instead
    matrixColumns: fieldType === 'matrix' ? [] : undefined, // Start empty, use count instead
    matrixRowsCount: fieldType === 'matrix' ? 10 : undefined,
    matrixColumnsCount: fieldType === 'matrix' ? 5 : undefined,
    matrixCellType: fieldType === 'matrix' ? 'text' : undefined, // 'radio' | 'checkbox' | 'text' | 'number'
    hideRowLabels: fieldType === 'matrix' ? true : undefined, // Default to hidden row labels
    matrixCellWidth: fieldType === 'matrix' ? 180 : undefined,
    matrixCellHeight: fieldType === 'matrix' ? 36 : undefined,
    matrixRowFontSize: fieldType === 'matrix' ? 14 : undefined,
    matrixColumnFontSize: fieldType === 'matrix' ? 14 : undefined,
    // Section defaults
    sectionSubtitle: fieldType === 'section' ? 'Optional subheading/description' : undefined,
    sectionDivider: fieldType === 'section' ? true : undefined,
    // Content field defaults
    content: fieldType === 'content' ? 'Enter your content here. You can add instructions, information, or any text that users should read.' : undefined,
      contactFields: fieldType === 'contact' ? [
        {
          id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_full_name`,
          type: 'text',
          label: 'Full Name',
          name: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_full_name`,
          required: true,
        },
        {
          id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_email`,
          type: 'email',
          label: 'Email Address',
          name: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_email`,
          required: true,
          validation: {
            pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
            message: 'Please enter a valid email address'
          }
        },
        {
          id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_phone`,
          type: 'text',
          label: 'Phone Number',
          name: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_phone`,
          required: false,
          validation: {
            pattern: '^[\\+]?[1-9][\\d]{0,15}$',
            message: 'Please enter a valid phone number'
          }
        },
        {
          id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_company`,
          type: 'text',
          label: 'Company/Organization',
          name: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_company`,
          required: false,
        },
        {
          id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_address`,
          type: 'textarea',
          label: 'Address',
          name: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_address`,
          required: false,
        }
      ] : undefined, // Contact fields for contact block
      fieldSize: fieldType === 'contact' ? 'medium' : undefined,
      columns: fieldType === 'table' ? [
        {
          key: 'action',
          label: 'Action',
          type: 'text',
          required: true,
          placeholder: 'Enter action'
        },
        {
          key: 'function',
          label: 'Function',
          type: 'text',
          required: false,
          placeholder: 'Enter function'
        },
        {
          key: 'owner',
          label: 'Owner(s)',
          type: 'text',
          required: false,
          placeholder: 'Enter owner'
        },
        {
          key: 'site_lead',
          label: 'Site lead (Ops Mgr)',
          type: 'text',
          required: false,
          placeholder: 'Enter site lead'
        },
        {
          key: 'due',
          label: 'Due',
          type: 'date',
          required: false
        },
        {
          key: 'type',
          label: 'Type',
          type: 'select',
          required: false,
          options: ['Task', 'Project', 'Meeting', 'Review', 'Other']
        },
        {
          key: 'cost',
          label: 'Cost KWD',
          type: 'number',
          required: false,
          placeholder: 'Enter cost'
        },
        {
          key: 'impact',
          label: 'Impact (Rev/Cost/Guest)',
          type: 'textarea',
          required: false,
          placeholder: 'Enter impact details'
        },
        {
          key: 'status',
          label: 'Status',
          type: 'select',
          required: false,
          options: ['Not Started', 'In Progress', 'Completed', 'On Hold', 'Cancelled']
        }
      ] : undefined, // Table columns for table field
      // Repeatable group defaults - start empty
      repeatableFields: fieldType === 'repeatable' ? [] : undefined,
      continueQuestion: fieldType === 'repeatable' ? 'Do you want to add another?' : undefined,
      continueYesLabel: fieldType === 'repeatable' ? 'Yes, add another' : undefined,
      continueNoLabel: fieldType === 'repeatable' ? 'No, submit form' : undefined,
      showContinueButton: fieldType === 'repeatable' ? true : undefined, // Show continue button by default
    };

    // Debug logging for section fields
    if (fieldType === 'section') {
      console.log('Creating section field:', newField);
    }

    setForm(prev => {
      const targetPosition = positionOverride !== null ? positionOverride : insertPosition;
      let newStructure;
      console.log('addField - resolved target position:', targetPosition);
      console.log('addField - form_structure length:', prev.form_structure.length);
      if (targetPosition !== null && targetPosition >= 0 && targetPosition <= prev.form_structure.length) {
        console.log('Inserting field at position:', targetPosition);
        newStructure = [
          ...prev.form_structure.slice(0, targetPosition),
          newField,
          ...prev.form_structure.slice(targetPosition)
        ];
      } else {
        // Add at the end (default behavior)
        console.log('Adding field at end');
        newStructure = [...prev.form_structure, newField];
      }
      
      const updatedForm = {
        ...prev,
        form_structure: newStructure,
      };
      console.log('Updated form structure:', updatedForm.form_structure);
      return updatedForm;
    });
    if (positionOverride !== null) {
      setInsertPosition(null);
    }
  };

  const updateField = (fieldId, updates) => {
    // Only log styling updates to reduce console spam
    if (updates.fontFamily || updates.marginTop || updates.marginBottom || updates.paddingTop || updates.paddingBottom) {
      console.log('FormBuilder: Updating field styling', fieldId, 'with updates:', updates);
    }
    setForm(prev => {
      const updatedForm = {
      ...prev,
      form_structure: prev.form_structure.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      ),
      };
      return updatedForm;
    });
  };

  const addContactField = (contactBlockId, contactFieldType) => {
    const newContactField = {
      id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${contactFieldType}`,
      type: contactFieldType,
      label: contactFieldType === 'text' ? 'Text' : 
             contactFieldType === 'email' ? 'Email' :
             contactFieldType === 'textarea' ? 'Text Area' :
             contactFieldType === 'number' ? 'Number' :
             contactFieldType === 'date' ? 'Date' :
             contactFieldType === 'radio' ? 'Gender' : 'Field',
      name: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${contactFieldType}`,
      required: false,
      options: contactFieldType === 'radio' ? ['Male', 'Female', 'Other', 'Prefer not to say'] : undefined,
    };

    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.map(field =>
        field.id === contactBlockId && field.type === 'contact'
          ? { ...field, contactFields: [...(field.contactFields || []), newContactField] }
          : field
      ),
    }));
  };

  const removeContactField = (contactBlockId, contactFieldId) => {
    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.map(field =>
        field.id === contactBlockId && field.type === 'contact'
          ? { ...field, contactFields: field.contactFields.filter(cf => cf.id !== contactFieldId) }
          : field
      ),
    }));
  };

  const updateContactField = (contactBlockId, contactFieldId, updates) => {
    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.map(field =>
        field.id === contactBlockId && field.type === 'contact'
          ? { 
              ...field, 
              contactFields: field.contactFields.map(cf =>
                cf.id === contactFieldId ? { ...cf, ...updates } : cf
              )
            }
          : field
      ),
    }));
  };

  const addRepeatableField = (repeatableBlockId, fieldType) => {
    const newRepeatableField = {
      id: `repeatable_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${fieldType}`,
      type: fieldType,
      label: fieldType === 'rating' ? 'Rate this item' 
        : fieldType === 'heading' ? 'Section heading' 
        : fieldType === 'subheading' ? 'Section subheading' 
        : fieldType === 'checkbox' ? 'Checkbox Field'
        : fieldType === 'number' ? 'Number Field'
        : fieldType === 'email' ? 'Email Field'
        : fieldType === 'date' ? 'Date Field'
        : fieldType === 'select' ? 'Dropdown Field'
        : fieldType === 'file' ? 'File Upload'
        : fieldType === 'mapLocation' ? 'Current location'
        : '',
      name: `repeatable_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${fieldType}`,
      required: false,
      level: fieldType === 'heading' ? 2 : fieldType === 'subheading' ? 4 : undefined,
      maxRating: fieldType === 'rating' ? 5 : undefined,
      ratingLabels: fieldType === 'rating' ? ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] : undefined,
      ratingIcon: fieldType === 'rating' ? 'star' : undefined,
      // Checkbox and select defaults
      options: (fieldType === 'checkbox' || fieldType === 'select') ? ['Option 1', 'Option 2'] : undefined,
      checkboxLayout: fieldType === 'checkbox' ? 'vertical' : undefined,
      // File upload defaults
      acceptedFileTypes: fieldType === 'file' ? ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'] : undefined,
      maxFileSize: fieldType === 'file' ? 10 : undefined,
      multipleFiles: fieldType === 'file' ? false : undefined,
    };

    console.log('Adding repeatable field:', {
      repeatableBlockId,
      fieldType,
      newRepeatableField,
      repeatableBlockId
    });

    setForm(prev => {
      const updatedForm = {
        ...prev,
        form_structure: prev.form_structure.map(field => {
          if (field.id === repeatableBlockId && field.type === 'repeatable') {
            const currentRepeatableFields = Array.isArray(field.repeatableFields) ? field.repeatableFields : [];
            const updatedRepeatableFields = [...currentRepeatableFields, newRepeatableField];
            console.log('Updating repeatable field:', {
              fieldId: field.id,
              currentCount: currentRepeatableFields.length,
              newCount: updatedRepeatableFields.length,
              newField: newRepeatableField
            });
            return { 
              ...field, 
              repeatableFields: updatedRepeatableFields
            };
          }
          return field;
        }),
      };
      console.log('Updated form structure:', updatedForm.form_structure);
      return updatedForm;
    });
  };

  const removeRepeatableField = (repeatableBlockId, repeatableFieldId) => {
    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.map(field =>
        field.id === repeatableBlockId && field.type === 'repeatable'
          ? { ...field, repeatableFields: field.repeatableFields.filter(rf => rf.id !== repeatableFieldId) }
          : field
      ),
    }));
  };

  const updateRepeatableField = (repeatableBlockId, repeatableFieldId, updates) => {
    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.map(field =>
        field.id === repeatableBlockId && field.type === 'repeatable'
          ? { 
              ...field, 
              repeatableFields: field.repeatableFields.map(rf =>
                rf.id === repeatableFieldId ? { ...rf, ...updates } : rf
              )
            }
          : field
      ),
    }));
  };

  const removeField = (fieldId) => {
    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.filter(field => field.id !== fieldId),
    }));
  };

  const changeFieldType = (fieldId, newType) => {
    setForm(prev => ({
      ...prev,
      form_structure: prev.form_structure.map(field => {
        if (field.id === fieldId) {
          // Create new field with same basic properties but new type
          const newField = {
            ...field,
            type: newType,
            // Reset type-specific properties
            options: newType === 'select' || newType === 'radio' || newType === 'checkbox' ? ['Option 1', 'Option 2'] : undefined,
            placeholder: newType === 'text' || newType === 'email' || newType === 'number' || newType === 'textarea' ? `Enter ${field.label.toLowerCase()}` : undefined,
            min: newType === 'number' || newType === 'slider' ? 0 : undefined,
            max: newType === 'number' || newType === 'slider' ? 100 : undefined,
            step: newType === 'number' || newType === 'slider' ? 1 : undefined,
            maxRating: newType === 'rating' ? 5 : undefined,
            ratingIcon: newType === 'rating' ? 'star' : undefined,
            // Remove properties that don't apply to new type
            ...(newType !== 'select' && newType !== 'radio' && newType !== 'checkbox' && { options: undefined }),
            ...(newType !== 'text' && newType !== 'email' && newType !== 'number' && newType !== 'textarea' && { placeholder: undefined }),
            ...(newType !== 'number' && newType !== 'slider' && { min: undefined, max: undefined, step: undefined }),
            ...(newType !== 'rating' && { maxRating: undefined, ratingIcon: undefined }),
            ...(newType !== 'mapLocation' && { mapDefaultLat: undefined, mapDefaultLng: undefined, mapZoom: undefined, mapHeight: undefined })
          };
          return newField;
        }
        return field;
      }),
    }));
    setChangeFieldTypeId(null);
  };

  const duplicateField = (fieldId) => {
    const fieldToDuplicate = form.form_structure.find(field => field.id === fieldId);
    if (fieldToDuplicate) {
      const duplicatedField = {
        ...fieldToDuplicate,
        id: Date.now().toString(),
        name: `${fieldToDuplicate.name}_copy`,
        label: `${fieldToDuplicate.label} (Copy)`,
      };

      const fieldIndex = form.form_structure.findIndex(field => field.id === fieldId);
      const newStructure = [...form.form_structure];
      newStructure.splice(fieldIndex + 1, 0, duplicatedField);

      setForm(prev => ({
        ...prev,
        form_structure: newStructure,
      }));
    }
  };

  const addOption = (fieldId) => {
    const field = form.form_structure.find(f => f.id === fieldId);
    if (field) {
      const newOptions = [...(field.options || []), `Choice ${(field.options?.length || 0) + 1}`];
      updateField(fieldId, { options: newOptions });
    }
  };

  const bulkAddOptions = (fieldId, text) => {
    const field = form.form_structure.find(f => f.id === fieldId);
    if (field && text) {
      // Split by newlines first, then by commas, then filter empty strings and trim
      const options = text
        .split(/\n|,/)
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0);
      
      if (options.length > 0) {
        const existingOptions = field.options || [];
        // Merge with existing options, avoiding duplicates
        const mergedOptions = [...existingOptions];
        options.forEach(opt => {
          if (!mergedOptions.includes(opt)) {
            mergedOptions.push(opt);
          }
        });
        updateField(fieldId, { options: mergedOptions });
      }
    }
  };

  const removeOption = (fieldId, optionIndex) => {
    const field = form.form_structure.find(f => f.id === fieldId);
    if (field && field.options) {
      const newOptions = field.options.filter((_, index) => index !== optionIndex);
      updateField(fieldId, { options: newOptions });
    }
  };

  const updateOption = (fieldId, optionIndex, newValue) => {
    const field = form.form_structure.find(f => f.id === fieldId);
    if (field && field.options) {
      const newOptions = [...field.options];
      newOptions[optionIndex] = newValue;
      updateField(fieldId, { options: newOptions });
    }
  };

  // Helper functions for repeatable field options
  const addRepeatableFieldOption = (repeatableBlockId, repeatableFieldId) => {
    const repeatableField = form.form_structure
      .find(f => f.id === repeatableBlockId && f.type === 'repeatable')
      ?.repeatableFields?.find(rf => rf.id === repeatableFieldId);
    
    if (repeatableField) {
      const currentOptions = repeatableField.options || [];
      const newOptions = [...currentOptions, `Choice ${currentOptions.length + 1}`];
      updateRepeatableField(repeatableBlockId, repeatableFieldId, { options: newOptions });
    }
  };

  const bulkAddRepeatableFieldOptions = (repeatableBlockId, repeatableFieldId, text) => {
    const repeatableField = form.form_structure
      .find(f => f.id === repeatableBlockId && f.type === 'repeatable')
      ?.repeatableFields?.find(rf => rf.id === repeatableFieldId);
    
    if (repeatableField && text) {
      // Split by newlines first, then by commas, then filter empty strings and trim
      const options = text
        .split(/\n|,/)
        .map(opt => opt.trim())
        .filter(opt => opt.length > 0);
      
      if (options.length > 0) {
        const existingOptions = repeatableField.options || [];
        // Merge with existing options, avoiding duplicates
        const mergedOptions = [...existingOptions];
        options.forEach(opt => {
          if (!mergedOptions.includes(opt)) {
            mergedOptions.push(opt);
          }
        });
        updateRepeatableField(repeatableBlockId, repeatableFieldId, { options: mergedOptions });
      }
    }
  };

  const removeRepeatableFieldOption = (repeatableBlockId, repeatableFieldId, optionIndex) => {
    const repeatableField = form.form_structure
      .find(f => f.id === repeatableBlockId && f.type === 'repeatable')
      ?.repeatableFields?.find(rf => rf.id === repeatableFieldId);
    
    if (repeatableField && repeatableField.options) {
      const newOptions = repeatableField.options.filter((_, index) => index !== optionIndex);
      updateRepeatableField(repeatableBlockId, repeatableFieldId, { options: newOptions });
    }
  };

  const updateRepeatableFieldOption = (repeatableBlockId, repeatableFieldId, optionIndex, newValue) => {
    const repeatableField = form.form_structure
      .find(f => f.id === repeatableBlockId && f.type === 'repeatable')
      ?.repeatableFields?.find(rf => rf.id === repeatableFieldId);
    
    if (repeatableField && repeatableField.options) {
      const newOptions = [...repeatableField.options];
      newOptions[optionIndex] = newValue;
      updateRepeatableField(repeatableBlockId, repeatableFieldId, { options: newOptions });
    }
  };

  const handleDragStart = (start) => {
    console.log('Drag start triggered:', start);
  };

  const handleDragEnd = (result) => {
    console.log('Drag end triggered:', result);
    console.log('Current form structure:', form.form_structure);
    try {
      if (!result.destination) {
        console.log('No destination, drag cancelled');
        return;
      }

      const { source, destination, draggableId } = result;

      if (source.droppableId.startsWith('repeatable-') || destination.droppableId.startsWith('repeatable-')) {
        const sourceRepeatableId = source.droppableId.replace('repeatable-', '');
        const destinationRepeatableId = destination.droppableId.replace('repeatable-', '');

        setForm(prev => {
          if (!prev.form_structure) return prev;

          let movedItem = null;

          const afterRemoval = prev.form_structure.map(field => {
            if (field.id === sourceRepeatableId) {
              const repeatableFields = Array.isArray(field.repeatableFields) ? [...field.repeatableFields] : [];
              movedItem = repeatableFields.splice(source.index, 1)[0];
              return {
                ...field,
                repeatableFields
              };
            }
            return field;
          });

          if (!movedItem) {
            console.error('Unable to locate moved repeatable field', { draggableId, source });
            return prev;
          }

          const afterInsertion = afterRemoval.map(field => {
            if (field.id === destinationRepeatableId) {
              const repeatableFields = Array.isArray(field.repeatableFields) ? [...field.repeatableFields] : [];
              repeatableFields.splice(destination.index, 0, movedItem);
              return {
                ...field,
                repeatableFields
              };
            }
            return field;
          });

          return {
            ...prev,
            form_structure: afterInsertion
          };
        });
        return;
      }

      // Validate form structure exists and is array
      if (!form.form_structure || !Array.isArray(form.form_structure)) {
        console.error('Invalid form structure for drag and drop');
        return;
      }

      // Try to find the field by ID first (more reliable than index)
      const draggedItem = form.form_structure.find(field => field.id === result.draggableId);
      const sourceIndex = form.form_structure.findIndex(field => field.id === result.draggableId);
      
      console.log('Dragged item found by ID:', draggedItem);
      console.log('Expected draggableId:', result.draggableId);
      console.log('Source index from ID search:', sourceIndex);
      console.log('Source index from result:', result.source.index);
      
      if (!draggedItem) {
        console.error('Dragged item not found by ID:', {
          expectedId: result.draggableId,
          formStructureLength: form.form_structure.length,
          availableIds: form.form_structure.map(f => f.id)
        });
        return;
      }
      
      // Use the found index instead of the result index
      const actualSourceIndex = sourceIndex;
      const actualDestinationIndex = result.destination.index;
      
      // Validate indices
      if (actualSourceIndex < 0 || actualSourceIndex >= form.form_structure.length) {
        console.error('Invalid source index:', actualSourceIndex);
        return;
      }
      
      if (actualDestinationIndex < 0 || actualDestinationIndex > form.form_structure.length) {
        console.error('Invalid destination index:', actualDestinationIndex);
        return;
      }

      const items = Array.from(form.form_structure);
      
      // Double-check that the item still exists at the expected index
      if (items[actualSourceIndex]?.id !== result.draggableId) {
        console.error('Field ID mismatch during reorder:', {
          expectedId: result.draggableId,
          actualId: items[actualSourceIndex]?.id,
          sourceIndex: actualSourceIndex
        });
        return;
      }
      
      const [reorderedItem] = items.splice(actualSourceIndex, 1);
      items.splice(actualDestinationIndex, 0, reorderedItem);

      setForm(prev => ({
        ...prev,
        form_structure: items,
      }));
    } catch (error) {
      console.error('Error in drag and drop:', error);
      toast.error('Failed to reorder fields. Please try again.');
    }
  };

  const saveForm = async () => {
    const existingFormId = currentFormIdRef.current;
    
    // For new forms, check if creation is in progress
    if (!existingFormId && creationInProgressRef.current) {
      toast.info('Save in progress, please wait...');
      return;
    }
    
    // Always save as personal form (not template)
    setSaveFolderId(selectedFolderId);
    await confirmSaveForm(false);
  };

  const confirmSaveForm = async (isTemplate = false) => {
    const existingFormId = currentFormIdRef.current;
    
    // Check if creation is already in progress (for new forms only)
    if (!existingFormId && creationInProgressRef.current) {
      toast.info('Save in progress, please wait...');
      return;
    }
    
    try {
      setSaving(true);
      
      const formData = {
        ...form,
        folder_id: saveFolderId,
        is_template: isTemplate,
        // Ensure these fields are included with correct conversion
        enable_multi_step: form.enableMultiStep === true || form.enableMultiStep === 1 || form.enableMultiStep === '1' || form.enableMultiStep === 'true',
        group_sections_with_questions: form.groupSectionsWithQuestions === true || form.groupSectionsWithQuestions === 1 || form.groupSectionsWithQuestions === '1' || form.groupSectionsWithQuestions === 'true',
        label_font_size: form.labelFontSize || '14px',
        daily_quota_limit: form.dailyQuotaLimit && form.dailyQuotaLimit > 0 ? parseInt(form.dailyQuotaLimit) : null,
        submission_start_date: form.submissionStartDate || null,
        submission_end_date: form.submissionEndDate || null
      };
      
      if (existingFormId) {
        // UPDATE existing form
        await formsAPI.update(existingFormId, formData);
        toast.success('Form updated successfully');
      } else {
        // CREATE new form
        creationInProgressRef.current = true;
        const response = await formsAPI.create(formData);
        const newFormId = response.data.data.id;
        
        // Store form ID in ref for future autosaves
        currentFormIdRef.current = newFormId;
        
        toast.success('Form saved successfully');
        
        // Update the selected folder in the form builder
        setSelectedFolderId(saveFolderId);
        
        // Redirect to edit mode for the new form
        window.location.href = `/builder/${newFormId}`;
      }
    } catch (error) {
      console.error('Save form error details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to save form: ${error.response?.data?.error || error.message}`);
      creationInProgressRef.current = false;
    } finally {
      setSaving(false);
    }
  };

  const openShareModal = async () => {
    const existingFormId = currentFormIdRef.current;
    if (!existingFormId) {
      // Auto-save the form first if it hasn't been saved
      try {
        await saveForm();
        // Wait a moment for the save to complete
        setTimeout(() => {
          setShareModal({ isOpen: true, form });
        }, 1000);
      } catch (error) {
        toast.error('Please save the form first before sharing');
        return;
      }
    } else {
      setShareModal({ isOpen: true, form });
    }
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, form: null });
  };

  const openUserShareModal = async () => {
    const existingFormId = currentFormIdRef.current;
    if (!existingFormId) {
      // Auto-save the form first if it hasn't been saved
      try {
        await saveForm();
        // Wait a moment for the save to complete
        setTimeout(() => {
          setUserShareModal({ isOpen: true, item: form });
        }, 1000);
      } catch (error) {
        toast.error('Please save the form first before sharing');
        return;
      }
    } else {
      setUserShareModal({ isOpen: true, item: form });
    }
  };

  const closeUserShareModal = () => {
    setUserShareModal({ isOpen: false, item: null });
  };

  const openFieldSelector = (event) => {
    console.log('openFieldSelector called with event:', event);
    const rect = event.target.getBoundingClientRect();
    const modalWidth = 900; // Updated to match new modal width
    const modalHeight = 500; // Estimated height
    
    // Calculate position to center the modal or keep it in viewport
    let left = rect.left + (rect.width / 2) - (modalWidth / 2);
    let top = rect.bottom + 15;
    
    // Adjust if modal would go off-screen horizontally
    if (left < 20) left = 20;
    if (left + modalWidth > window.innerWidth - 20) {
      left = window.innerWidth - modalWidth - 20;
    }
    
    // If modal would go below viewport, position above the button
    if (top + modalHeight > window.innerHeight - 20) {
      top = rect.top - modalHeight - 15;
      // If still doesn't fit above, center vertically
      if (top < 20) {
        top = Math.max(20, (window.innerHeight - modalHeight) / 2);
      }
    }
    
    // Ensure modal stays within viewport bounds
    left = Math.max(20, Math.min(left, window.innerWidth - modalWidth - 20));
    top = Math.max(20, Math.min(top, window.innerHeight - modalHeight - 20));
    
    // If the modal is too constrained, center it on screen
    if (window.innerWidth < modalWidth + 40) {
      left = (window.innerWidth - modalWidth) / 2;
    }
    if (window.innerHeight < modalHeight + 40) {
      top = (window.innerHeight - modalHeight) / 2;
    }
    
    console.log('Setting field selector position:', { top, left });
    setFieldSelectorPosition({ top, left });
    setShowFieldSelector(true);
    console.log('Field selector should now be open');
  };

  const closeFieldSelector = () => {
    setShowFieldSelector(false);
  };



  const selectFieldType = (fieldType) => {
    console.log('selectFieldType called with:', fieldType);
    addField(fieldType, insertPosition);
    setInsertPosition(null);
    closeFieldSelector();
  };

  const renderFieldPreview = (field) => {
    const fieldProps = {
      disabled: true,
    };

    switch (field.type) {
      case 'heading':
      case 'section':
        const level = field.level || 2;
        const headingStyle = {
          fontSize: level === 1 ? '28px' : level === 2 ? '24px' : level === 3 ? '20px' : level === 4 ? '18px' : level === 5 ? '16px' : '14px',
          fontWeight: field.bold ? '700' : '600',
          fontStyle: field.italic ? 'italic' : 'normal',
          textDecoration: field.underline ? 'underline' : 'none',
          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
          color: field.textColor || '#1f2937',
          textAlign: field.alignment || 'left',
          margin: '16px 0 8px 0',
          padding: '8px 0',
          borderBottom: '2px solid transparent',
          border: 'none',
          background: 'transparent',
          width: '100%',
          outline: 'none'
        };
        return (
          <input
            type="text"
            value={field.label}
            onChange={(e) => updateField(field.id, { label: e.target.value })}
            style={headingStyle}
            placeholder="Enter section title"
          />
        );
      
      case 'subheading':
        const subheadingLevel = field.level || 4;
        const subheadingStyle = {
          fontSize: subheadingLevel === 1 ? '28px' : subheadingLevel === 2 ? '24px' : subheadingLevel === 3 ? '20px' : subheadingLevel === 4 ? '18px' : subheadingLevel === 5 ? '16px' : '14px',
          fontWeight: field.bold ? '600' : '500',
          fontStyle: field.italic ? 'italic' : 'normal',
          textDecoration: field.underline ? 'underline' : 'none',
          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
          color: field.textColor || '#4b5563',
          textAlign: field.alignment || 'left',
          margin: '12px 0 8px 0',
          padding: '4px 0',
          border: 'none',
          background: 'transparent',
          width: '100%',
          outline: 'none'
        };
        return (
          <input
            type="text"
            value={field.label}
            onChange={(e) => updateField(field.id, { label: e.target.value })}
            style={subheadingStyle}
            placeholder="Enter subheading"
          />
        );
      
      case 'text':
      case 'email':
      case 'number':
      case 'date':
      case 'textarea':
      case 'select':
        return null;
      
      case 'radio':
        return (
          <div style={{ 
            padding: '10px', 
            border: '1px solid #d1d5db', 
            borderRadius: '6px', 
            backgroundColor: '#f9fafb',
            color: '#6b7280',
            fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
            fontWeight: field.bold ? '700' : 'normal',
            fontStyle: field.italic ? 'italic' : 'normal',
            paddingLeft: field.inputPaddingLeft || 16
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #d1d5db', display: 'inline-block' }} />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Option 1</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #d1d5db', display: 'inline-block' }} />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Option 2</span>
              </div>
            </div>
          </div>
        );
      
      case 'checkbox':
        const checkboxLayout = field.checkboxLayout || 'vertical';
        return (
          <div style={{ 
            padding: '10px', 
            border: '1px solid #d1d5db', 
            borderRadius: '6px', 
            backgroundColor: '#f9fafb',
            color: '#6b7280',
            fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
            fontWeight: field.bold ? '700' : 'normal',
            fontStyle: field.italic ? 'italic' : 'normal',
            paddingLeft: field.inputPaddingLeft || 16
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: checkboxLayout === 'horizontal' ? 'row' : 'column',
              flexWrap: checkboxLayout === 'horizontal' ? 'wrap' : 'nowrap',
              gap: '6px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '12px', height: '12px', border: '2px solid #d1d5db', display: 'inline-block' }} />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Option 1</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '12px', height: '12px', border: '2px solid #d1d5db', display: 'inline-block' }} />
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Option 2</span>
              </div>
            </div>
          </div>
        );
      
      case 'calculation':
        const formattedValue = field.currency ? '$0.00' : '0.00';
        return (
          <div style={{ 
            padding: '10px', 
            border: '1px solid #d1d5db', 
            borderRadius: '6px', 
            backgroundColor: '#f9fafb',
            color: '#6b7280'
          }}>
            Calculation field: {field.label} ({formattedValue})
          </div>
        );
      
      case 'rating':
        const maxRating = field.maxRating || 5;
        const ratingLabels = field.ratingLabels || ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
        const ratingIcon = field.ratingIcon || 'star';
        // Use Unicode characters that respond to CSS color where possible
        const iconMap = { 
          star: { filled: '★', empty: '☆' },
          heart: { filled: '❤', empty: '♡' },
          thumbs: { filled: '👍', empty: '👍' },
          face: { filled: '😊', empty: '😊' },
          fire: { filled: '🔥', empty: '🔥' },
          check: { filled: '✔', empty: '☐' }
        };
        const iconSet = iconMap[ratingIcon] || iconMap.star;
        const displayIcon = iconSet.filled;
        return (
          <div style={{ 
            padding: '15px', 
            border: '2px solid rgba(0, 0, 0, 0.2)', 
            borderRadius: '12px', 
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            color: '#374151'
          }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '12px',
              color: 'black'
            }}>
              {displayIcon} {field.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              {Array.from({ length: maxRating }, (_, i) => (
                <span 
                  key={i}
                  style={{ 
                    fontSize: '20px', 
                    color: '#d1d5db',
                    cursor: 'default'
                  }}
                >
                  {displayIcon}
                </span>
              ))}
            </div>
            {ratingLabels && ratingLabels.length > 0 && (
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '8px'
              }}>
                <span>{ratingLabels[0]}</span>
                <span>{ratingLabels[ratingLabels.length - 1]}</span>
              </div>
            )}
          </div>
        );
      
      case 'file':
        const acceptedTypes = field.acceptedFileTypes || ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
        const maxSize = field.maxFileSize || 10;
        const allowMultiple = field.multipleFiles || false;
        return (
          <div style={{ 
            padding: '15px', 
            border: '2px solid rgba(0, 0, 0, 0.2)', 
            borderRadius: '12px', 
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            color: '#374151'
          }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              marginBottom: '12px',
              color: 'black'
            }}>
              📎 {field.label}
              {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
            </div>
            <div style={{
              border: '2px dashed rgba(0, 0, 0, 0.3)',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📤</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Click to upload or drag and drop
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                {acceptedTypes.join(', ').toUpperCase()} up to {maxSize}MB
                {allowMultiple && ' (Multiple files allowed)'}
              </div>
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#6b7280',
              textAlign: 'center'
            }}>
              Supported formats: {acceptedTypes.join(', ')}
            </div>
          </div>
        );
      
      case 'contact':
        // Render contact block preview
        return (
          <div>
            
           
          </div>
        );
      
      case 'matrix':
        const previewCols = Array.isArray(field.matrixColumns) && field.matrixColumns.length > 0
          ? field.matrixColumns
          : Array.from({ length: Number.isFinite(field.matrixColumnsCount) ? Math.max(1, field.matrixColumnsCount) : 5 }, (_, i) => `Col ${i + 1}`);
        return (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {!field.hideRowLabels && <div style={{ width: '180px', padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#374151' }}> </div>}
              {previewCols.map((c, i) => (
                <div key={i} style={{ flex: 1, padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#374151', borderLeft: '1px solid #e5e7eb' }}>
                  {typeof c === 'string' ? c : (c.label || c.key)}
                </div>
              ))}
            </div>
            {(() => {
              const previewRows = Array.isArray(field.matrixRows) && field.matrixRows.length > 0
                ? field.matrixRows
                : Array.from({ length: Number.isFinite(field.matrixRowsCount) ? Math.max(1, field.matrixRowsCount) : 10 }, (_, i) => `Row ${i + 1}`);
              return previewRows.slice(0, 3).map((r, ri) => (
                <div key={ri} style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                  {!field.hideRowLabels && (
                    <div style={{ width: '180px', padding: '8px 12px', color: '#374151', background: '#fcfcfd', fontWeight: 500 }}>
                      {typeof r === 'string' ? r : (r.label || r.key)}
                    </div>
                  )}
                  {previewCols.map((_, ci) => (
                    <div key={ci} style={{ flex: 1, padding: '8px 12px', borderLeft: '1px solid #e5e7eb', color: '#6b7280' }}>
                      {field.matrixCellType === 'radio' ? '◉' : field.matrixCellType === 'checkbox' ? '☑' : 'input'}
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        );

      case 'slider':
        return (
          <div style={{ padding: '10px' }}>
            <div style={{ marginBottom: '6px', color: '#6b7280', fontSize: '12px' }}>Slider preview ({field.min ?? 0}–{field.max ?? 10})</div>
            <input type="range" min={field.min ?? 0} max={field.max ?? 10} step={field.step ?? 1} disabled style={{ width: '100%' }} />
          </div>
        );

      case 'mapLocation':
        return (
          <div style={{ padding: '8px 0' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Current location — respondents use their device GPS</div>
            <MapLocationField
              value={null}
              onChange={() => {}}
              disabled
            />
          </div>
        );

      case 'repeatable':
        return (
          <div style={{ 
            border: '2px dashed #3b82f6', 
            borderRadius: '12px', 
            padding: '16px', 
            background: 'rgba(59, 130, 246, 0.05)',
            marginBottom: '16px'
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '600', 
              marginBottom: '8px',
              color: '#1f2937',
              fontSize: '14px'
            }}>
              <FiRefreshCw style={{ color: '#3b82f6' }} />
              {field.label || 'Repeatable Group'}
              {field.repeatableFields && field.repeatableFields.length > 0 && (
                <span style={{ 
                  fontSize: '11px', 
                  fontWeight: '500', 
                  color: '#6b7280',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  padding: '2px 6px',
                  borderRadius: '8px'
                }}>
                  {field.repeatableFields.length} {field.repeatableFields.length === 1 ? 'field' : 'fields'}
                </span>
              )}
            </div>
            {field.repeatableFields && Array.isArray(field.repeatableFields) && field.repeatableFields.length > 0 ? (
              <div style={{ 
                padding: '12px',
                background: 'white',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                fontSize: '12px',
                color: '#6b7280',
                maxHeight: 'none',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {/* Debug: Show count */}
                <div style={{ 
                  fontSize: '10px', 
                  color: '#9ca3af', 
                  marginBottom: '4px',
                  padding: '4px',
                  background: '#f3f4f6',
                  borderRadius: '4px'
                }}>
                  DEBUG: {field.repeatableFields.length} fields found
                </div>
                {field.repeatableFields.map((rf, idx) => {
                  console.log(`Rendering repeatable field ${idx}:`, rf);
                  if (!rf || !rf.type) {
                    console.warn('Invalid repeatable field at index', idx, rf);
                    return null;
                  }
                  
                  const fieldKey = `rf_${field.id}_${idx}_${rf.id || idx}`;
                  
                  // Handle heading/subheading
                  if (rf.type === 'heading' || rf.type === 'subheading') {
                    const headingLevel = rf.level || (rf.type === 'heading' ? 2 : 4);
                    const headingSize = headingLevel === 1 ? '16px' : headingLevel === 2 ? '14px' : headingLevel === 3 ? '13px' : headingLevel === 4 ? '12px' : headingLevel === 5 ? '11px' : '10px';
                    return (
                      <div 
                        key={fieldKey} 
                        style={{ 
                          padding: '6px 0',
                          minHeight: '24px',
                          display: 'block',
                          width: '100%',
                          borderBottom: idx < field.repeatableFields.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}
                      >
                        <div style={{ 
                          fontSize: headingSize, 
                          fontWeight: rf.type === 'heading' ? '700' : '600', 
                          color: rf.type === 'heading' ? '#1f2937' : '#374151',
                          display: 'block'
                        }}>
                          {rf.label || (rf.type === 'heading' ? 'Heading' : 'Subheading')}
                        </div>
                      </div>
                    );
                  }
                  
                  // Handle regular fields
                  return (
                    <div 
                      key={fieldKey} 
                      style={{ 
                        padding: '6px 0',
                        minHeight: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        borderBottom: idx < field.repeatableFields.length - 1 ? '1px solid #e5e7eb' : 'none'
                      }}
                    >
                      <span style={{ 
                        flex: '1',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#374151',
                        marginRight: '8px'
                      }}>
                        {rf.label || 'Unnamed Field'}
                      </span>
                      {rf.required && (
                        <span style={{ color: '#ef4444', marginRight: '6px', fontSize: '12px' }}>*</span>
                      )}
                      <span style={{ 
                        fontSize: '10px', 
                        color: '#9ca3af', 
                        fontStyle: 'italic'
                      }}>
                        ({rf.type || 'unknown'})
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ 
                padding: '8px',
                background: '#f9fafb',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#6b7280',
                fontStyle: 'italic'
              }}>
                No fields added
              </div>
            )}
          </div>
        );

      case 'section':
        console.log('Rendering section field preview:', field);
        return (
          <div style={{ margin: '12px 0 8px 0', border: '2px dashed #e5e7eb', borderRadius: '8px', padding: '16px', background: '#f9fafb' }}>
            <div style={{
              fontSize: (
                field.level === 1 ? '28px' :
                field.level === 2 ? '24px' :
                field.level === 3 ? '20px' :
                field.level === 4 ? '18px' :
                field.level === 5 ? '16px' :
                field.level === 6 ? '14px' : '20px'
              ),
              fontWeight: 700,
              color: '#111827',
              marginBottom: '6px'
            }}>
              <input
                type="text"
                value={field.label || ''}
                onChange={(e) => updateField(field.id, { label: e.target.value })}
                placeholder="Section title"
                style={{ border: 'none', background: 'transparent', width: '100%', outline: 'none', font: 'inherit', color: 'inherit' }}
              />
            </div>
            <div>
              <input
                type="text"
                value={field.sectionSubtitle || ''}
                onChange={(e) => updateField(field.id, { sectionSubtitle: e.target.value })}
                placeholder="Optional subheading"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', color: '#374151', fontSize: '13px' }}
              />
            </div>
            {field.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
          </div>
        );

      case 'content':
        return (
          <div style={{ margin: '12px 0 8px 0', border: '2px dashed #e5e7eb', borderRadius: '8px', padding: '16px', background: '#f9fafb' }}>
            <textarea
              value={field.content || ''}
              onChange={(e) => updateField(field.id, { content: e.target.value })}
              placeholder="Enter your content here. You can add instructions, information, or any text that users should read."
              style={{ 
                width: '100%', 
                minHeight: '100px',
                padding: '12px', 
                border: '1px solid #e5e7eb', 
                borderRadius: '6px', 
                background: '#fff', 
                color: '#374151', 
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                lineHeight: '1.5'
              }}
            />
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
              This content will be displayed to users as plain text. Use it for instructions, information, or any static content.
            </div>
          </div>
        );

      case 'table':
        // Render table preview
        return (
          <div style={{ 
            border: '2px dashed rgba(0, 0, 0, 0.3)', 
            borderRadius: '16px', 
            padding: '20px', 
            background: 'rgba(0, 0, 0, 0.02)',
            marginBottom: '16px',
            position: 'relative'
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '700', 
              marginBottom: '16px',
              color: 'black',
              fontSize: '16px'
            }}>
              📊 Table Form
            </div>
            <div style={{ 
              border: '1px solid rgba(0, 0, 0, 0.2)', 
              borderRadius: '8px', 
              overflow: 'hidden',
              backgroundColor: 'rgba(255, 255, 255, 0.5)'
            }}>
              {/* Table Header */}
              <div style={{ 
                display: 'flex', 
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.2)'
              }}>
                {field.columns?.slice(0, 4).map((column, index) => (
                  <div key={index} style={{ 
                    flex: 1, 
                    padding: '8px 12px', 
                    fontSize: '12px', 
                    fontWeight: '600',
                    color: '#374151',
                    borderRight: index < 3 ? '1px solid rgba(0, 0, 0, 0.2)' : 'none'
                  }}>
                    {column.label}
                  </div>
                ))}
                {field.columns?.length > 4 && (
                  <div style={{ 
                    flex: 1, 
                    padding: '8px 12px', 
                    fontSize: '12px', 
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    +{field.columns.length - 4} more
                  </div>
                )}
              </div>
              {/* Sample rows */}
              {[1, 2].map((rowIndex) => (
                <div key={rowIndex} style={{ 
                  display: 'flex', 
                  borderBottom: rowIndex < 2 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'
                }}>
                  {field.columns?.slice(0, 4).map((column, colIndex) => (
                    <div key={colIndex} style={{ 
                      flex: 1, 
                      padding: '8px 12px', 
                      fontSize: '11px', 
                      color: '#6b7280',
                      borderRight: colIndex < 3 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'
                    }}>
                      {column.type === 'date' ? '2024-01-01' : 
                       column.type === 'number' ? '100' :
                       column.type === 'select' ? 'Option' : 'Sample text'}
                    </div>
                  ))}
                  {field.columns?.length > 4 && (
                    <div style={{ 
                      flex: 1, 
                      padding: '8px 12px', 
                      fontSize: '11px', 
                      color: '#6b7280'
                    }}>
                      ...
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#6b7280',
              textAlign: 'center',
              marginTop: '8px'
            }}>
              {field.columns?.length || 0} columns • Users can add/remove rows
            </div>
          </div>
        );
      
      default:
        return (
          <div style={{ 
            padding: '10px', 
            border: '1px solid #d1d5db', 
            borderRadius: '6px', 
            backgroundColor: '#f9fafb',
            color: '#6b7280'
          }}>
            {field.type} field: {field.label}
          </div>
        );
    }
  };

  const renderUserPreview = (field) => {
    if (field.type === 'contact') {
      // Render contact block with all its contact fields
      return (
        <div style={{ 
          border: '2px dashed black', 
          borderRadius: '16px', 
          padding: '20px', 
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)',
          marginBottom: '16px',
          position: 'relative'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '700', 
            marginBottom: '16px',
            color: 'black',
            fontSize: '16px'
          }}>
            Contact Information
            <span style={{ 
              fontSize: '12px', 
              fontWeight: '500', 
              color: '#6b7280',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              padding: '2px 8px',
              borderRadius: '12px'
            }}>
              {field.contactFields?.length || 0} fields
            </span>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: field.fieldSize === 'small' 
              ? 'repeat(auto-fit, minmax(150px, 1fr))' 
              : field.fieldSize === 'large' 
                ? 'repeat(auto-fit, minmax(300px, 1fr))'
                : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: field.fieldSize === 'small' ? '8px' : field.fieldSize === 'large' ? '16px' : '12px'
          }}>
            {field.contactFields?.map((contactField) => (
              <div key={contactField.id} style={{ 
                padding: field.fieldSize === 'small' ? '8px' : field.fieldSize === 'large' ? '16px' : '12px', 
                border: '1px solid rgba(0, 0, 0, 0.2)', 
                borderRadius: field.fieldSize === 'small' ? '8px' : field.fieldSize === 'large' ? '16px' : '12px', 
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.2s ease'
              }}>
                {contactField.type === 'radio' ? (
                  <>
                    <div style={{ 
                      fontWeight: '600', 
                      marginBottom: '8px',
                      color: '#1f2937',
                      fontSize: '14px'
                    }}>
                      {contactField.label} {contactField.required && <span style={{ color: '#ef4444' }}>*</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {contactField.options && contactField.options.map((option, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }}>
                          <input 
                            type="radio" 
                            disabled 
                            style={{ margin: 0, accentColor: 'black' }} 
                          />
                          <span style={{ fontSize: '13px', color: '#374151' }}>{option}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : contactField.type === 'textarea' ? (
                  <>
                    <div style={{ 
                      fontWeight: '600', 
                      marginBottom: '6px',
                      color: '#1f2937',
                      fontSize: '14px'
                    }}>
                      {contactField.label} {contactField.required && <span style={{ color: '#ef4444' }}>*</span>}
                    </div>
                    <textarea
                      disabled
                      placeholder={`Enter ${contactField.label.toLowerCase()}`}
                      style={{
                        width: '100%',
                        minHeight: field.fieldSize === 'small' ? '60px' : field.fieldSize === 'large' ? '120px' : '80px',
                        padding: field.fieldSize === 'small' ? '6px 8px' : field.fieldSize === 'large' ? '12px 16px' : '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: field.fieldSize === 'small' ? '4px' : field.fieldSize === 'large' ? '8px' : '6px',
                        fontSize: field.fieldSize === 'small' ? '12px' : field.fieldSize === 'large' ? '15px' : '13px',
                        backgroundColor: '#f9fafb',
                        resize: 'vertical'
                      }}
                    />
                  </>
                ) : (
                  <>
                    <div style={{ 
                      fontWeight: '600', 
                      marginBottom: '6px',
                      color: '#1f2937',
                      fontSize: '14px'
                    }}>
                      {contactField.label} {contactField.required && <span style={{ color: '#ef4444' }}>*</span>}
                    </div>
                    <input
                      type={contactField.type === 'email' ? 'email' : contactField.type === 'number' ? 'number' : contactField.type === 'date' ? 'date' : 'text'}
                      disabled
                      placeholder={`Enter ${contactField.label.toLowerCase()}`}
                      style={{
                        width: '100%',
                        padding: field.fieldSize === 'small' ? '6px 8px' : field.fieldSize === 'large' ? '12px 16px' : '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: field.fieldSize === 'small' ? '4px' : field.fieldSize === 'large' ? '8px' : '6px',
                        fontSize: field.fieldSize === 'small' ? '12px' : field.fieldSize === 'large' ? '15px' : '13px',
                        backgroundColor: '#f9fafb'
                      }}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
          
          {(!field.contactFields || field.contactFields.length === 0) && (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              <span style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}>📝</span>
              No contact fields added yet
              <br />
              <span style={{ fontSize: '12px' }}>Add fields to collect contact information</span>
            </div>
          )}
        </div>
      );
    }
    
    if (field.type === 'rating') {
      const maxRating = field.maxRating || 5;
      const ratingLabels = field.ratingLabels || ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
      return (
        <div style={{ 
          padding: '15px', 
          border: '2px solid rgba(0, 0, 0, 0.2)', 
          borderRadius: '12px', 
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          color: '#374151',
          marginBottom: '16px'
        }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            marginBottom: '12px',
            color: 'black'
          }}>
            {field.label}
            {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {Array.from({ length: maxRating }, (_, i) => (
              <span 
                key={i}
                style={{ 
                  fontSize: '20px', 
                  color: '#fbbf24',
                  cursor: 'default'
                }}
              >
                ⭐
              </span>
            ))}
          </div>
          {ratingLabels && ratingLabels.length > 0 && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '8px'
            }}>
              <span>{ratingLabels[0]}</span>
              <span>{ratingLabels[ratingLabels.length - 1]}</span>
            </div>
          )}
        </div>
      );
    }
    
    if (field.type === 'file') {
      const acceptedTypes = field.acceptedFileTypes || ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
      const maxSize = field.maxFileSize || 10;
      const allowMultiple = field.multipleFiles || false;
      return (
        <div style={{ 
          padding: '15px', 
          border: '2px solid rgba(0, 0, 0, 0.2)', 
          borderRadius: '12px', 
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          color: '#374151',
          marginBottom: '16px'
        }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            marginBottom: '12px',
            color: 'black'
          }}>
            {field.label}
            {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
          </div>
          <div style={{
            border: '2px dashed rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            marginBottom: '8px'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📤</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Click to upload or drag and drop
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
              {acceptedTypes.join(', ').toUpperCase()} up to {maxSize}MB
              {allowMultiple && ' (Multiple files allowed)'}
            </div>
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Supported formats: {acceptedTypes.join(', ')}
          </div>
        </div>
      );
    }
    
    // For other field types, return simple preview
    return (
      <div style={{ 
        padding: '10px', 
        border: '1px solid #d1d5db', 
        borderRadius: '6px', 
        backgroundColor: '#f9fafb',
        color: '#6b7280'
      }}>
        {field.type} field: {field.label}
        {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <BuilderContainer>
        <LoadingSpinner>Loading form...</LoadingSpinner>
      </BuilderContainer>
    );
  }

  return (
    <>
      {/* Steps are now displayed in the top nav when on builder route */}

      <BuilderContainer>

      <MainContent>
        <MainPanel>
         
        <FormPreview>
          {currentStep === 1 && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <FormTitle
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="enter form title"
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
               
                <FormDescription
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add form description (optional)"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Submit button text (optional)
                </label>
                <input
                  type="text"
                  value={form.submit_button_text || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, submit_button_text: e.target.value }))}
                  placeholder="e.g. Submit Feedback (leave empty for &quot;Submit Form&quot;)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.9)'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Folder (Optional):
                </label>
                <select
                  value={selectedFolderId || ''}
                  onChange={(e) => setSelectedFolderId(e.target.value || null)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="">No folder</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>
                      📁 {folder.name}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Organize your forms by putting them in folders
                </div>
              </div>
              
              <div style={{ marginTop: '16px' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Company Logo:
                </label>
                <ImageUpload
                  value={form.logo_url || ''}
                  onChange={(url) => setForm(prev => ({ ...prev, logo_url: url }))}
                />
              </div>

              {/* Removed multi-step toggle from first section per user request */}

              {/* Field Label Font Size Option */}
              <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Field Label Font Size
                </label>
                <select
                  value={form.labelFontSize || '14px'}
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, labelFontSize: e.target.value }));
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.3s ease',
                    color: '#374151'
                  }}
                >
                  <option value="12px">Small (12px)</option>
                  <option value="14px">Medium (14px)</option>
                  <option value="16px">Large (16px)</option>
                  <option value="18px">Extra Large (18px)</option>
                  <option value="20px">XXL (20px)</option>
                </select>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Choose the font size for field labels in your form
                </div>
              </div>

              {/* Daily Quota Limit Option */}
              <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Daily Submission Limit (Optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.dailyQuotaLimit || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    setForm(prev => ({ ...prev, dailyQuotaLimit: value }));
                  }}
                  placeholder="Enter daily limit (e.g., 10)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.3s ease',
                    color: '#374151'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Set a daily limit for form submissions. After reaching the limit, the form will be inactive until the next day. Leave empty for unlimited submissions.
                </div>
              </div>

              {/* Date Limit Options */}
              <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '12px', display: 'block' }}>
                  Submission Date Range (Optional)
                </label>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                    Start Date
                  </label>
                  <input
                    type="datetime-local"
                    value={form.submissionStartDate ? new Date(form.submissionStartDate).toISOString().slice(0, 16) : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : null;
                      setForm(prev => ({ ...prev, submissionStartDate: value }));
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      transition: 'all 0.3s ease',
                      color: '#374151'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                    End Date
                  </label>
                  <input
                    type="datetime-local"
                    value={form.submissionEndDate ? new Date(form.submissionEndDate).toISOString().slice(0, 16) : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : null;
                      setForm(prev => ({ ...prev, submissionEndDate: value }));
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      transition: 'all 0.3s ease',
                      color: '#374151'
                    }}
                  />
                </div>
                
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Restrict form submissions to a specific date range. Users can only submit the form between the start and end dates. Leave empty for no date restrictions.
                </div>
              </div>
              
              {/* Next Button */}
              <div style={{ marginTop: '32px', textAlign: 'center' }}>
                <button
                  onClick={() => setCurrentStep(2)}
                  style={{
                    background: 'black',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#374151'}
                  onMouseOut={(e) => e.target.style.background = 'black'}
                >
                  Next
                  <FiChevronRight />
                </button>
              </div>
            </>
          )}

          <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            {currentStep === 2 && (
              <div>
                {form.form_structure.length === 0 && (
                  <EmptyState>
                    <EmptyStateIcon>🚀</EmptyStateIcon>
                    <EmptyStateTitle>Ready to Build Your Form?</EmptyStateTitle>
                    <EmptyStateDescription>
                      Create amazing forms by adding fields using the buttons below. Choose from text inputs, dropdowns, checkboxes, and more. You can drag and drop to reorder fields anytime.
                    </EmptyStateDescription>
                    <EmptyStateActions>
                      <QuickActionButton onClick={(e) => {
                        setCurrentStep(2);
                        // Center the field selector modal
                        setFieldSelectorPosition({ 
                          top: Math.max(20, (window.innerHeight - 500) / 2), 
                          left: Math.max(20, (window.innerWidth - 900) / 2) 
                        });
                        setShowFieldSelector(true);
                      }}>
                        <FiPlus />
                        Add Question
                      </QuickActionButton>
                      <QuickActionButton onClick={() => {
                        setCurrentStep(2);
                        addField('section', form.form_structure.length);
                      }}>
                        <FiType />
                        Create Section
                      </QuickActionButton>
                    </EmptyStateActions>
                  </EmptyState>
                )}
            {(() => {
              console.log('Current step:', currentStep);
              return currentStep === 2;
            })() && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={oneQuestionMode}
                      onChange={(e) => {
                        setOneQuestionMode(e.target.checked);
                        if (e.target.checked) setFocusedFieldIndex(0);
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>One-question-at-a-time</span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>(focus edit)</span>
                  </label>
                  {oneQuestionMode && form.form_structure.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setFocusedFieldIndex(i => Math.max(0, i - 1))}
                        disabled={focusedFieldIndex === 0}
                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', background: 'white', color: '#374151', cursor: focusedFieldIndex === 0 ? 'not-allowed' : 'pointer' }}
                        title="Previous question"
                      >
                        ← Prev
                      </button>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {focusedFieldIndex + 1} / {form.form_structure.length}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFocusedFieldIndex(i => Math.min(form.form_structure.length - 1, i + 1))}
                        disabled={focusedFieldIndex >= form.form_structure.length - 1}
                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', background: 'white', color: '#374151', cursor: focusedFieldIndex >= form.form_structure.length - 1 ? 'not-allowed' : 'pointer' }}
                        title="Next question"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
                {form.form_structure.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={form.enableMultiStep === true}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        console.log('Multi-step checkbox changed:', enabled, 'Current state:', form.enableMultiStep);
                        setForm(prev => {
                          const updated = {
                          ...prev,
                          enableMultiStep: enabled,
                            enable_multi_step: enabled, // Also update backend field name for consistency
                            groupSectionsWithQuestions: enabled ? true : false, // Set to false when disabling
                            group_sections_with_questions: enabled ? true : false // Also update backend field name
                          };
                          console.log('Updated form state - enableMultiStep:', updated.enableMultiStep, 'groupSectionsWithQuestions:', updated.groupSectionsWithQuestions);
                          return updated;
                        });
                      }}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>Multi-step (by sections)</span>
                  </label>
                </div>
                )}
              <Droppable droppableId="form-fields" key="form-fields">
                {(provided) => (
                  <FieldList ref={provided.innerRef} {...provided.droppableProps}>
                    {(() => {
                      console.log('Rendering field list with form_structure:', form.form_structure);
                    const indices = oneQuestionMode ? [focusedFieldIndex].filter(i => i >= 0 && i < form.form_structure.length) : form.form_structure.map((_, i) => i);
                    return indices.map((index) => {
                      const field = form.form_structure[index];
                        // Validate field has valid ID
                        if (!field || !field.id) {
                          console.error('Invalid field at index', index, field);
                          return null;
                        }
                        
                        // Debug logging for field rendering order
                        console.log(`Rendering field at index ${index}:`, field.type, field.label);
                      
                      // Debug logging for section fields
                      if (field.type === 'section') {
                        console.log('Rendering section field at index', index, field);
                      }
                      
                      // Check for duplicate IDs
                      const duplicateIndex = form.form_structure.findIndex((f, i) => i !== index && f.id === field.id);
                      if (duplicateIndex !== -1) {
                        console.error('Duplicate field ID found:', field.id, 'at indices', index, 'and', duplicateIndex);
                        return null;
                      }
                      
                      return (
                    <Draggable 
                      key={field.id} 
                      draggableId={field.id} 
                      index={index}
                      isDragDisabled={false}
                    >
                      {(provided, snapshot) => (
                        <FieldItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          isDragging={snapshot.isDragging}
                          fieldWidth={field.fieldWidth || 'full'}
                          fieldType={field.type}
                        >
                          <FieldHeader>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              flex: 1
                            }}>
                              <div {...provided.dragHandleProps} style={{ 
                                cursor: 'grab', 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '8px',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s ease',
                                border: '1px solid transparent',
                                minWidth: '32px',
                                justifyContent: 'center'
                              }} onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                                e.target.style.borderColor = '#d1d5db';
                              }} onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = 'transparent';
                              }}>
                                <span style={{ 
                                  fontSize: '18px', 
                                  color: '#9ca3af',
                                  userSelect: 'none',
                                  fontWeight: 'bold',
                                  lineHeight: 1
                                }}>⋮⋮</span>
                              </div>
                              {field.type !== 'heading' && field.type !== 'section' && (
                                <FieldLabelInput
                                  value={field.label}
                                  onChange={(e) => updateField(field.id, { label: e.target.value })}
                                />
                              )}
                            </div>
                            <FieldActions>
                              {field.type !== 'section' && (
                                <FieldActionButton
                                  onClick={() => {
                                    // Insert a new Section right before this field
                                    console.log('Adding section before field at index:', index);
                                    console.log('Current field:', field);
                                    addField('section', index);
                                  }}
                                  title="Add section above this field"
                                  style={{ backgroundColor: '#f0f9ff', borderColor: '#0ea5e9' }}
                                >
                                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>+</span>
                                </FieldActionButton>
                              )}
                              <FieldActionButton
                                onClick={() => duplicateField(field.id)}
                                title="Duplicate field"
                              >
                                <FiCopy />
                              </FieldActionButton>
                              <FieldActionButton
                                onClick={() => updateField(field.id, { required: !field.required })}
                                title="Toggle required"
                              >
                                {field.required ? <FiCheckSquare /> : <FiSquare />}
                              </FieldActionButton>
                              <FieldActionButton
                                onClick={() => {
                                  const fieldIndex = form.form_structure.findIndex(f => f.id === field.id);
                                  setInsertPosition(fieldIndex + 1);
                                  setShowFieldSelector(true);
                                }}
                                title="Add new field after this one"
                              >
                                <FiPlus />
                              </FieldActionButton>
                              <FieldActionButton
                                onClick={() => setChangeFieldTypeId(field.id)}
                                title="Change field type"
                              >
                                <FiEdit3 />
                              </FieldActionButton>
                              <FieldActionButton
                                onClick={() => removeField(field.id)}
                                title="Delete field"
                              >
                                <FiTrash2 />
                              </FieldActionButton>
                            </FieldActions>
                          </FieldHeader>

                          {(() => {
                            const fieldPreview = renderFieldPreview(field);
                            return fieldPreview != null
                              ? <FieldInput>{fieldPreview}</FieldInput>
                              : null;
                          })()}

                          {/* Inline adders removed per request */}


                          {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                            <OptionsContainer>
                              <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                                Options:
                              </label>
                              {field.options?.map((option, index) => (
                                <OptionItem key={index}>
                                  <OptionInput
                                    value={option}
                                    onChange={(e) => updateOption(field.id, index, e.target.value)}
                                    placeholder={`Choice ${index + 1}`}
                                  />
                                  <RemoveOptionButton
                                    onClick={() => removeOption(field.id, index)}
                                    title="Remove option"
                                  >
                                    <FiX />
                                  </RemoveOptionButton>
                                </OptionItem>
                              )) || []}
                              <AddOptionButton
                                onClick={() => addOption(field.id)}
                                title="Add new choice"
                              >
                                <FiPlus />
                                Add Choice
                              </AddOptionButton>
                              <div style={{ marginTop: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
                                <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', display: 'block', fontWeight: '500' }}>
                                  Bulk Add Options (paste multiple options):
                                </label>
                                <textarea
                                  placeholder="Paste options here, one per line or separated by commas&#10;Example:&#10;Option 1&#10;Option 2&#10;Option 3&#10;&#10;Or: Option 1, Option 2, Option 3"
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    bulkAddOptions(field.id, pastedText);
                                    e.target.value = '';
                                  }}
                                  onChange={(e) => {
                                    // Allow manual typing and Enter key
                                    if (e.target.value.includes('\n') || e.target.value.includes(',')) {
                                      bulkAddOptions(field.id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.ctrlKey) {
                                      e.preventDefault();
                                      bulkAddOptions(field.id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    minHeight: '80px',
                                    padding: '8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                  }}
                                />
                                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                  Paste options separated by newlines or commas. Press Ctrl+Enter to add.
                                </div>
                              </div>
                              {field.type === 'select' && (
                                <div style={{ marginTop: '12px', padding: '12px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                  <label style={{ fontSize: '12px', color: '#1e40af', marginBottom: '6px', display: 'block', fontWeight: '500' }}>
                                    Max participants per option (e.g. per training date):
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={9999}
                                    value={field.maxPerOption ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                                      updateField(field.id, { maxPerOption: Number.isFinite(v) && v > 0 ? v : undefined });
                                    }}
                                    placeholder="e.g. 35 — leave empty for no limit"
                                    style={{
                                      width: '100%',
                                      padding: '8px 12px',
                                      border: '1px solid #93c5fd',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      background: 'white'
                                    }}
                                  />
                                  <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '4px' }}>
                                    When set, each option (e.g. each training date) will accept at most this many submissions. Useful for session capacity limits.
                                  </div>
                                  <div
                                    style={{
                                      marginTop: '14px',
                                      paddingTop: '14px',
                                      borderTop: '1px solid #bfdbfe',
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '10px',
                                        fontSize: '13px',
                                        color: '#1e3a8a',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!field.showOptionBookingCounts}
                                        onChange={(e) => updateField(field.id, { showOptionBookingCounts: e.target.checked })}
                                        style={{ width: '16px', height: '16px', marginTop: '2px', accentColor: '#1d4ed8', flexShrink: 0, cursor: 'pointer' }}
                                      />
                                      <span>
                                        Show booking count on each option
                                        <span style={{ display: 'block', fontSize: '11px', fontWeight: '400', color: '#64748b', marginTop: '4px' }}>
                                          Example: &quot;15 March 2026 (12 booked)&quot; on the live form, from submitted responses.
                                        </span>
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              )}
                            </OptionsContainer>
                          )}

                          {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                            <div style={{ 
                              marginTop: '12px',
                              padding: '12px',
                              border: '1px dashed rgba(0, 0, 0, 0.2)',
                              borderRadius: '8px',
                              backgroundColor: 'rgba(0, 0, 0, 0.02)'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                marginBottom: '8px'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={field.allowComment || false}
                                  onChange={(e) => updateField(field.id, { allowComment: e.target.checked })}
                                  style={{ 
                                    width: '16px',
                                    height: '16px',
                                    accentColor: 'black',
                                    cursor: 'pointer'
                                  }}
                                />
                                <label style={{ 
                                  fontSize: '13px', 
                                  color: '#374151',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}>
                                  💬 Allow additional comments
                                </label>
                              </div>
                              {field.allowComment && (
                                <div style={{ marginTop: '8px' }}>
                                  <label style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280', 
                                    display: 'block', 
                                    marginBottom: '4px',
                                    fontWeight: '500'
                                  }}>
                                    Comment field label:
                                  </label>
                                  <input
                                    type="text"
                                    value={field.commentLabel || 'Additional Comments'}
                                    onChange={(e) => updateField(field.id, { commentLabel: e.target.value })}
                                    placeholder="Additional Comments"
                                    style={{
                                      width: '100%',
                                      padding: '8px 10px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      backgroundColor: 'white'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Conditional Fields Configuration for Select Dropdown */}
                          {(field.type === 'select' || field.type === 'checkbox') && (
                            <div style={{ 
                              marginTop: '16px',
                              padding: '16px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: '#f9fafb'
                            }}>
                              <div 
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px',
                                  marginBottom: '12px',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                                onClick={() => toggleSection(field.id, 'conditional')}
                              >
                                {isSectionExpanded(field.id, 'conditional') ? (
                                  <FiChevronDown size={16} color="#374151" />
                                ) : (
                                  <FiChevronRight size={16} color="#374151" />
                                )}
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                  🔀 Conditional Fields
                                </span>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {field.type === 'checkbox' ? 'Show fields when option is checked' : 'Show fields based on selected option'}
                                </span>
                              </div>
                              
                              {isSectionExpanded(field.id, 'conditional') && (
                                <>
                                  {field.options?.map((option, optIndex) => {
                                    const conditionalFields = field.conditionalOptions?.[option] || [];
                                    const isEnabled = conditionalFields.length > 0 || field.conditionalOptions?.[option] !== undefined;
                                    const isOptionExpanded = isSectionExpanded(field.id, 'conditional_option', optIndex);
                                    
                                    return (
                                      <div key={optIndex} style={{ 
                                        marginBottom: '12px',
                                        padding: '12px',
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb'
                                      }}>
                                        <div 
                                          style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                          }}
                                          onClick={() => toggleSection(field.id, 'conditional_option', optIndex)}
                                        >
                                          {isOptionExpanded ? (
                                            <FiChevronDown size={14} color="#6b7280" />
                                          ) : (
                                            <FiChevronRight size={14} color="#6b7280" />
                                          )}
                                          <input
                                            type="checkbox"
                                            checked={isEnabled}
                                            onChange={(e) => {
                                          if (e.target.checked) {
                                            // Enable conditional fields - initialize with empty array
                                            const updatedConditionalOptions = {
                                              ...(field.conditionalOptions || {}),
                                              [option]: []
                                            };
                                            setForm(prev => ({
                                              ...prev,
                                              form_structure: prev.form_structure.map(f => 
                                                f.id === field.id 
                                                  ? { ...f, conditionalOptions: updatedConditionalOptions }
                                                  : f
                                              )
                                            }));
                                            // Auto-expand the section when enabled
                                            setTimeout(() => {
                                              toggleSection(field.id, 'conditional_option', optIndex);
                                            }, 0);
                                          } else {
                                            // Disable conditional fields - remove option and clear field references
                                            const updatedConditionalOptions = { ...(field.conditionalOptions || {}) };
                                            delete updatedConditionalOptions[option];
                                            
                                            // Remove conditionalField and conditionalValue from affected fields
                                            const updatedFields = form.form_structure.map(f => {
                                              if (f.conditionalField === field.id && f.conditionalValue === option) {
                                                const { conditionalField, conditionalValue, ...rest } = f;
                                                return rest;
                                              }
                                              return f;
                                            });
                                            
                                            setForm(prev => ({
                                              ...prev,
                                              form_structure: updatedFields.map(f => 
                                                f.id === field.id 
                                                  ? { ...f, conditionalOptions: updatedConditionalOptions }
                                                  : f
                                              )
                                            }));
                                          }
                                          e.stopPropagation(); // Prevent toggling the section
                                        }}
                                        style={{ 
                                          width: '18px',
                                          height: '18px',
                                          accentColor: 'black',
                                          cursor: 'pointer',
                                          flexShrink: 0
                                        }}
                                        onClick={(e) => e.stopPropagation()} // Prevent toggling when clicking checkbox
                                      />
                                      <label 
                                        style={{ 
                                          fontSize: '13px', 
                                          fontWeight: '500', 
                                          color: '#374151',
                                          cursor: 'pointer',
                                          flex: 1
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent toggling when clicking label
                                          // Toggle checkbox when clicking label
                                          const checkbox = e.currentTarget.previousElementSibling;
                                          if (checkbox) checkbox.click();
                                        }}
                                      >
                                        {field.type === 'checkbox' ? `When "${option}" is checked, show fields:` : `When "${option}" is selected, show fields:`}
                                      </label>
                                    </div>
                                    
                                    {isOptionExpanded && (
                                      <div style={{ marginTop: '10px', paddingLeft: '24px' }}>
                                        {isEnabled ? (
                                          <>
                                            <select
                                              multiple
                                              size={Math.min(5, form.form_structure.filter(f => 
                                                f.id !== field.id && 
                                                f.type !== 'section' && 
                                                f.type !== 'heading'
                                              ).length)}
                                              value={conditionalFields}
                                              onChange={(e) => {
                                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                const updatedConditionalOptions = {
                                                  ...(field.conditionalOptions || {}),
                                                  [option]: selected
                                                };
                                                
                                                // Update dependent fields to reference this field
                                                const allSelectedFields = Object.values(updatedConditionalOptions).flat();
                                                const updatedFields = form.form_structure.map(f => {
                                                  if (selected.includes(f.id)) {
                                                    return { ...f, conditionalField: field.id, conditionalValue: option };
                                                  } else if (f.conditionalField === field.id && f.conditionalValue === option && !selected.includes(f.id)) {
                                                    const { conditionalField, conditionalValue, ...rest } = f;
                                                    return rest;
                                                  } else if (f.conditionalField === field.id && f.conditionalValue !== option) {
                                                    // Keep other conditional values
                                                    return f;
                                                  }
                                                  return f;
                                                });
                                                
                                                setForm(prev => ({
                                                  ...prev,
                                                  form_structure: updatedFields.map(f => 
                                                    f.id === field.id 
                                                      ? { ...f, conditionalOptions: updatedConditionalOptions }
                                                      : f
                                                  )
                                                }));
                                              }}
                                              style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                backgroundColor: 'white'
                                              }}
                                            >
                                              {form.form_structure
                                                .filter(f => 
                                                  f.id !== field.id && 
                                                  f.type !== 'section' && 
                                                  f.type !== 'heading' &&
                                                  f.type !== 'contact'
                                                )
                                                .map(f => (
                                                  <option key={f.id} value={f.id}>
                                                    {f.label || f.name || 'Unnamed Field'}
                                                  </option>
                                                ))}
                                            </select>
                                            <div style={{ 
                                              fontSize: '11px', 
                                              color: '#6b7280', 
                                              marginTop: '6px',
                                              fontStyle: 'italic'
                                            }}>
                                              Hold Ctrl/Cmd to select multiple fields
                                            </div>
                                          </>
                                        ) : (
                                          <div style={{ 
                                            fontSize: '12px', 
                                            color: '#6b7280', 
                                            fontStyle: 'italic',
                                            padding: '8px',
                                            backgroundColor: '#f9fafb',
                                            borderRadius: '4px',
                                            border: '1px dashed #d1d5db'
                                          }}>
                                            ✓ Check the box above to enable conditional fields for this option
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              </>
                              )}
                            </div>
                          )}

                          {/* Nested Dropdown Configuration */}
                          {field.type === 'select' && (
                            <div style={{ 
                              marginTop: '16px',
                              padding: '16px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: '#f9fafb'
                            }}>
                              <div 
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '8px',
                                  marginBottom: '12px',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                                onClick={() => toggleSection(field.id, 'nested')}
                              >
                                {isSectionExpanded(field.id, 'nested') ? (
                                  <FiChevronDown size={16} color="#374151" />
                                ) : (
                                  <FiChevronRight size={16} color="#374151" />
                                )}
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                  🔽 Nested Dropdown Options
                                </span>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  Show a dependent dropdown when option is selected
                                </span>
                              </div>
                              
                              {isSectionExpanded(field.id, 'nested') && (
                                <>
                                  {field.options?.map((option, optIndex) => {
                                    const nestedOptions = field.nestedOptions?.[option] || [];
                                    const isEnabled = nestedOptions.length > 0 || field.nestedOptions?.[option] !== undefined;
                                    const isOptionExpanded = isSectionExpanded(field.id, 'nested_option', optIndex);
                                    
                                    return (
                                      <div key={optIndex} style={{ 
                                        marginBottom: '12px',
                                        padding: '12px',
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb'
                                      }}>
                                        <div 
                                          style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                          }}
                                          onClick={() => toggleSection(field.id, 'nested_option', optIndex)}
                                        >
                                          {isOptionExpanded ? (
                                            <FiChevronDown size={14} color="#6b7280" />
                                          ) : (
                                            <FiChevronRight size={14} color="#6b7280" />
                                          )}
                                          <input
                                            type="checkbox"
                                            checked={isEnabled}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                // Enable nested options - initialize with empty array
                                                const updatedNestedOptions = {
                                                  ...(field.nestedOptions || {}),
                                                  [option]: []
                                                };
                                                setForm(prev => ({
                                                  ...prev,
                                                  form_structure: prev.form_structure.map(f => 
                                                    f.id === field.id 
                                                      ? { ...f, nestedOptions: updatedNestedOptions }
                                                      : f
                                                  )
                                                }));
                                                // Auto-expand the section when enabled
                                                setTimeout(() => {
                                                  toggleSection(field.id, 'nested_option', optIndex);
                                                }, 0);
                                              } else {
                                                // Disable nested options
                                                const updatedNestedOptions = { ...(field.nestedOptions || {}) };
                                                delete updatedNestedOptions[option];
                                                
                                                setForm(prev => ({
                                                  ...prev,
                                                  form_structure: prev.form_structure.map(f => 
                                                    f.id === field.id 
                                                      ? { ...f, nestedOptions: updatedNestedOptions }
                                                      : f
                                                  )
                                                }));
                                              }
                                              e.stopPropagation(); // Prevent toggling the section
                                            }}
                                            style={{ 
                                              width: '18px',
                                              height: '18px',
                                              accentColor: 'black',
                                              cursor: 'pointer',
                                              flexShrink: 0
                                            }}
                                            onClick={(e) => e.stopPropagation()} // Prevent toggling when clicking checkbox
                                          />
                                          <label 
                                            style={{ 
                                              fontSize: '13px', 
                                              fontWeight: '500', 
                                              color: '#374151',
                                              cursor: 'pointer',
                                              flex: 1
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation(); // Prevent toggling when clicking label
                                              // Toggle checkbox when clicking label
                                              const checkbox = e.currentTarget.previousElementSibling;
                                              if (checkbox) checkbox.click();
                                            }}
                                          >
                                            When "{option}" is selected, show nested dropdown with:
                                          </label>
                                        </div>
                                        
                                        {isOptionExpanded && (
                                          <div style={{ marginTop: '10px', paddingLeft: '24px' }}>
                                            {isEnabled ? (
                                              <>
                                                <div style={{ 
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  gap: '6px'
                                                }}>
                                                  {nestedOptions.map((nestedOpt, nestedIndex) => (
                                                    <div key={nestedIndex} style={{ 
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '8px'
                                                    }}>
                                                      <input
                                                        type="text"
                                                        value={nestedOpt}
                                                        onChange={(e) => {
                                                          const updatedNestedOptions = {
                                                            ...(field.nestedOptions || {}),
                                                            [option]: nestedOptions.map((opt, idx) => 
                                                              idx === nestedIndex ? e.target.value : opt
                                                            )
                                                          };
                                                          setForm(prev => ({
                                                            ...prev,
                                                            form_structure: prev.form_structure.map(f => 
                                                              f.id === field.id 
                                                                ? { ...f, nestedOptions: updatedNestedOptions }
                                                                : f
                                                            )
                                                          }));
                                                        }}
                                                        placeholder={`Nested option ${nestedIndex + 1}`}
                                                        style={{
                                                          flex: 1,
                                                          padding: '6px 10px',
                                                          border: '1px solid #d1d5db',
                                                          borderRadius: '4px',
                                                          fontSize: '12px',
                                                          backgroundColor: 'white'
                                                        }}
                                                      />
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          const updatedNestedOptions = {
                                                            ...(field.nestedOptions || {}),
                                                            [option]: nestedOptions.filter((_, idx) => idx !== nestedIndex)
                                                          };
                                                          // Clean up empty arrays
                                                          if (updatedNestedOptions[option].length === 0) {
                                                            delete updatedNestedOptions[option];
                                                          }
                                                          setForm(prev => ({
                                                            ...prev,
                                                            form_structure: prev.form_structure.map(f => 
                                                              f.id === field.id 
                                                                ? { ...f, nestedOptions: updatedNestedOptions }
                                                                : f
                                                            )
                                                          }));
                                                        }}
                                                        style={{
                                                          padding: '6px 10px',
                                                          background: '#fee2e2',
                                                          border: '1px solid #fca5a5',
                                                          borderRadius: '4px',
                                                          color: '#991b1b',
                                                          cursor: 'pointer',
                                                          fontSize: '12px'
                                                        }}
                                                      >
                                                        <FiX />
                                                      </button>
                                                    </div>
                                                  ))}
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const updatedNestedOptions = {
                                                        ...(field.nestedOptions || {}),
                                                        [option]: [...nestedOptions, '']
                                                      };
                                                      // Clean up empty arrays
                                                      if (updatedNestedOptions[option].length === 0) {
                                                        delete updatedNestedOptions[option];
                                                      }
                                                      setForm(prev => ({
                                                        ...prev,
                                                        form_structure: prev.form_structure.map(f => 
                                                          f.id === field.id 
                                                            ? { ...f, nestedOptions: updatedNestedOptions }
                                                            : f
                                                        )
                                                      }));
                                                    }}
                                                    style={{
                                                      padding: '6px 10px',
                                                      background: '#fee2e2',
                                                      border: '1px solid #fca5a5',
                                                      borderRadius: '4px',
                                                      color: '#991b1b',
                                                      cursor: 'pointer',
                                                      fontSize: '12px'
                                                    }}
                                                  >
                                                    <FiX />
                                                  </button>
                                                </div>
                                                <div style={{ 
                                                  marginTop: '8px',
                                                  padding: '8px',
                                                  backgroundColor: '#eff6ff',
                                                  borderRadius: '4px',
                                                  fontSize: '11px',
                                                  color: '#1e40af'
                                                }}>
                                                  💡 To use this nested dropdown, create another dropdown field and configure it to depend on this field.
                                                </div>
                                              </>
                                            ) : (
                                              <div style={{ 
                                                fontSize: '12px', 
                                                color: '#6b7280', 
                                                fontStyle: 'italic',
                                                padding: '8px',
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '4px',
                                                border: '1px dashed #d1d5db'
                                              }}>
                                                ✓ Check the box above to enable nested options for this option
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  
                                  {/* Configure this dropdown to be a nested dropdown */}
                                  <div style={{ 
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: 'white',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    <div 
                                      style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                      }}
                                      onClick={() => toggleSection(field.id, 'parent_dropdown')}
                                    >
                                      {isSectionExpanded(field.id, 'parent_dropdown') ? (
                                        <FiChevronDown size={14} color="#6b7280" />
                                      ) : (
                                        <FiChevronRight size={14} color="#6b7280" />
                                      )}
                                      <input
                                        type="checkbox"
                                        checked={!!field.parentDropdown}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            // Find available parent dropdowns
                                            const parentDropdowns = form.form_structure.filter(f => 
                                              f.type === 'select' && 
                                              f.id !== field.id &&
                                              f.nestedOptions && 
                                              Object.keys(f.nestedOptions).length > 0
                                            );
                                            if (parentDropdowns.length > 0) {
                                              // Set the first available parent as default
                                              setForm(prev => ({
                                                ...prev,
                                                form_structure: prev.form_structure.map(f => 
                                                  f.id === field.id 
                                                    ? { ...f, parentDropdown: parentDropdowns[0].id }
                                                    : f
                                                )
                                              }));
                                            }
                                          } else {
                                            // Remove parent dropdown
                                            setForm(prev => ({
                                              ...prev,
                                              form_structure: prev.form_structure.map(f => 
                                                f.id === field.id 
                                                  ? { ...f, parentDropdown: undefined }
                                                  : f
                                              )
                                            }));
                                          }
                                          e.stopPropagation(); // Prevent toggling the section
                                        }}
                                        style={{ 
                                          width: '18px',
                                          height: '18px',
                                          accentColor: 'black',
                                          cursor: 'pointer',
                                          flexShrink: 0
                                        }}
                                        onClick={(e) => e.stopPropagation()} // Prevent toggling when clicking checkbox
                                      />
                                      <label 
                                        style={{ 
                                          fontSize: '13px', 
                                          fontWeight: '500', 
                                          color: '#374151',
                                          cursor: 'pointer',
                                          flex: 1
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent toggling when clicking label
                                          // Toggle checkbox when clicking label
                                          const checkbox = e.currentTarget.previousElementSibling;
                                          if (checkbox) checkbox.click();
                                        }}
                                      >
                                        This dropdown depends on another dropdown
                                      </label>
                                    </div>
                                    
                                    {isSectionExpanded(field.id, 'parent_dropdown') && field.parentDropdown && (
                                      <div style={{ marginTop: '10px', paddingLeft: '24px' }}>
                                        <label style={{ 
                                          fontSize: '12px', 
                                          color: '#6b7280', 
                                          display: 'block',
                                          marginBottom: '6px',
                                          fontWeight: '500'
                                        }}>
                                          Parent Dropdown:
                                        </label>
                                        <select
                                          value={field.parentDropdown || ''}
                                          onChange={(e) => {
                                            setForm(prev => ({
                                              ...prev,
                                              form_structure: prev.form_structure.map(f => 
                                                f.id === field.id 
                                                  ? { ...f, parentDropdown: e.target.value || undefined }
                                                  : f
                                              )
                                            }));
                                          }}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            backgroundColor: 'white'
                                          }}
                                        >
                                          <option value="">-- Select Parent Dropdown --</option>
                                          {form.form_structure
                                            .filter(f => 
                                              f.type === 'select' && 
                                              f.id !== field.id &&
                                              f.nestedOptions && 
                                              Object.keys(f.nestedOptions).length > 0
                                            )
                                            .map(f => (
                                              <option key={f.id} value={f.id}>
                                                {f.label || f.name || 'Unnamed Dropdown'}
                                              </option>
                                            ))}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          <FieldSettingsContainer>
                            {(field.type === 'text' || field.type === 'email' || field.type === 'number' || field.type === 'date' || field.type === 'textarea' || field.type === 'select' || field.type === 'radio' || field.type === 'checkbox' || field.type === 'mapLocation') && (
                              <>
                              <SettingGroup>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                  <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                                    <SettingLabel>Field Width:</SettingLabel>
                                    <Select
                                      value={field.fieldWidth || 'full'}
                                      onChange={(e) => updateField(field.id, { fieldWidth: e.target.value })}
                                      style={{ background: 'white', marginTop: '4px', width: '100%' }}
                                    >
                                      <option value="full">Full Width (1 field per row)</option>
                                      <option value="half">Half Width (2 fields per row)</option>
                                      <option value="two-thirds">Two Thirds (1.5 fields per row)</option>
                                      <option value="third">One Third (3 fields per row)</option>
                                      <option value="quarter">Quarter (4 fields per row)</option>
                                    </Select>
                                  </div>
                                  {field.type !== 'mapLocation' && (
                                  <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                                    <SettingLabel>Font Family:</SettingLabel>
                                    <Select
                                      value={field.fontFamily || 'default'}
                                      onChange={(e) => updateField(field.id, { fontFamily: e.target.value })}
                                      style={{ background: 'white', marginTop: '4px', width: '100%' }}
                                    >
                                      <option value="default">Default</option>
                                      <option value="Arial">Arial</option>
                                      <option value="Helvetica">Helvetica</option>
                                      <option value="Times New Roman">Times New Roman</option>
                                      <option value="Georgia">Georgia</option>
                                      <option value="Verdana">Verdana</option>
                                      <option value="Courier New">Courier New</option>
                                      <option value="Impact">Impact</option>
                                      <option value="Comic Sans MS">Comic Sans MS</option>
                                      <option value="Trebuchet MS">Trebuchet MS</option>
                                      <option value="Palatino">Palatino</option>
                                      <option value="Garamond">Garamond</option>
                                    </Select>
                                  </div>
                                  )}
                                </div>
                              </SettingGroup>

                            {(field.type === 'text' || field.type === 'email' || field.type === 'number' || field.type === 'date' || field.type === 'textarea' || field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                                <SettingGroup>
                                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ flex: '1 1 200px' }}>
                                      <SettingLabel>Text Formatting:</SettingLabel>
                                      <div style={{ display: 'flex', gap: '12px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                                          <input
                                            type="checkbox"
                                            checked={field.bold || false}
                                            onChange={(e) => updateField(field.id, { bold: e.target.checked })}
                                            style={{ margin: 0 }}
                                          />
                                          Bold
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                                          <input
                                            type="checkbox"
                                            checked={field.italic || false}
                                            onChange={(e) => updateField(field.id, { italic: e.target.checked })}
                                            style={{ margin: 0 }}
                                          />
                                          Italic
                                        </label>
                                      </div>
                                    </div>
                                    <div style={{ flex: '0 1 120px' }}>
                                      <SettingLabel>Input field padding (left):</SettingLabel>
                                      <SettingInput
                                        type="number"
                                        value={field.inputPaddingLeft || 16}
                                        onChange={(e) => updateField(field.id, { inputPaddingLeft: parseInt(e.target.value) || 16 })}
                                        placeholder="16"
                                        style={{ fontSize: '12px', padding: '4px 6px', marginTop: '4px', width: '100%' }}
                                      />
                                    </div>
                                  </div>
                                </SettingGroup>
                            )}

                              </>
                            )}

                            {field.type === 'checkbox' && (
                              <SettingGroup>
                                <SettingLabel>Display Layout:</SettingLabel>
                                <Select
                                  value={field.checkboxLayout || 'vertical'}
                                  onChange={(e) => updateField(field.id, { checkboxLayout: e.target.value })}
                                  style={{ background: 'white', marginTop: '4px', width: '100%' }}
                                >
                                  <option value="vertical">Line by Line (Vertical)</option>
                                  <option value="horizontal">In a Row (Horizontal)</option>
                                </Select>
                              </SettingGroup>
                            )}

                            {(field.type === 'heading' || field.type === 'section' || field.type === 'subheading') ? (
                              <>
                                <SettingGroup>
                                  <SettingLabel>Heading Level:</SettingLabel>
                                  <Select
                                    value={field.level || 2}
                                    onChange={(e) => updateField(field.id, { level: parseInt(e.target.value) })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value={1}>H1 - Largest</option>
                                    <option value={2}>H2 - Large</option>
                                    <option value={3}>H3 - Medium</option>
                                    <option value={4}>H4 - Small</option>
                                    <option value={5}>H5 - Smaller</option>
                                    <option value={6}>H6 - Smallest</option>
                                  </Select>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Text Alignment:</SettingLabel>
                                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                    <button
                                      type="button"
                                      onClick={() => updateField(field.id, { alignment: 'left' })}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: (field.alignment || 'left') === 'left' ? '#3b82f6' : 'white',
                                        color: (field.alignment || 'left') === 'left' ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Left
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateField(field.id, { alignment: 'center' })}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: field.alignment === 'center' ? '#3b82f6' : 'white',
                                        color: field.alignment === 'center' ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Center
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateField(field.id, { alignment: 'right' })}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: field.alignment === 'right' ? '#3b82f6' : 'white',
                                        color: field.alignment === 'right' ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Right
                                    </button>
                                  </div>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Text Formatting:</SettingLabel>
                                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                    <button
                                      type="button"
                                      onClick={() => updateField(field.id, { 
                                        bold: !field.bold 
                                      })}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: field.bold ? '#3b82f6' : 'white',
                                        color: field.bold ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      Bold
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateField(field.id, { 
                                        italic: !field.italic 
                                      })}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: field.italic ? '#3b82f6' : 'white',
                                        color: field.italic ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      Italic
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateField(field.id, { 
                                        underline: !field.underline 
                                      })}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: field.underline ? '#3b82f6' : 'white',
                                        color: field.underline ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        textDecoration: 'underline'
                                      }}
                                    >
                                      Underline
                                    </button>
                                  </div>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Font Family:</SettingLabel>
                                  <Select
                                    value={field.fontFamily || 'default'}
                                    onChange={(e) => updateField(field.id, { fontFamily: e.target.value })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value="default">Default</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Verdana">Verdana</option>
                                    <option value="Courier New">Courier New</option>
                                    <option value="Impact">Impact</option>
                                    <option value="Comic Sans MS">Comic Sans MS</option>
                                    <option value="Trebuchet MS">Trebuchet MS</option>
                                    <option value="Palatino">Palatino</option>
                                    <option value="Garamond">Garamond</option>
                                  </Select>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Text Color:</SettingLabel>
                                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px', alignItems: 'center' }}>
                                    <input
                                      type="color"
                                      value={field.textColor || '#1f2937'}
                                      onChange={(e) => updateField(field.id, { textColor: e.target.value })}
                                      style={{ width: '40px', height: '32px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                      {field.textColor || '#1f2937'}
                                    </span>
                                  </div>
                                </SettingGroup>

                                {field.type === 'section' && (
                                  <>
                                    <SettingGroup>
                                      <SettingLabel>Section Subtitle:</SettingLabel>
                                      <SettingInput
                                        type="text"
                                        value={field.sectionSubtitle || ''}
                                        onChange={(e) => updateField(field.id, { sectionSubtitle: e.target.value })}
                                        placeholder="Optional subheading/description"
                                        style={{ fontSize: '12px', padding: '6px 8px' }}
                                      />
                                    </SettingGroup>
                                    
                                    <SettingGroup>
                                      <SettingLabel>Section Options:</SettingLabel>
                                      <div style={{ marginTop: '4px' }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                                    <input
                                      type="checkbox"
                                      checked={!!field.sectionDivider}
                                      onChange={(e) => updateField(field.id, { sectionDivider: e.target.checked })}
                                    />
                                    Show divider line under section
                                  </label>
                                      </div>
                                </SettingGroup>
                              </>
                            )}
                              </>
                            ) : null}

                            {field.type === 'slider' && (
                              <>
                                <SettingGroup>
                                  <SettingLabel>Font Family:</SettingLabel>
                                  <Select
                                    value={field.fontFamily || 'default'}
                                    onChange={(e) => updateField(field.id, { fontFamily: e.target.value })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value="default">Default</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Verdana">Verdana</option>
                                    <option value="Courier New">Courier New</option>
                                    <option value="Impact">Impact</option>
                                    <option value="Comic Sans MS">Comic Sans MS</option>
                                    <option value="Trebuchet MS">Trebuchet MS</option>
                                    <option value="Palatino">Palatino</option>
                                    <option value="Garamond">Garamond</option>
                                  </Select>
                                </SettingGroup>

                                <SettingGroup>
                                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ flex: '1 1 200px' }}>
                                      <SettingLabel>Text Formatting:</SettingLabel>
                                      <div style={{ display: 'flex', gap: '12px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                                          <input
                                            type="checkbox"
                                            checked={field.bold || false}
                                            onChange={(e) => updateField(field.id, { bold: e.target.checked })}
                                            style={{ margin: 0 }}
                                          />
                                          Bold
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                                          <input
                                            type="checkbox"
                                            checked={field.italic || false}
                                            onChange={(e) => updateField(field.id, { italic: e.target.checked })}
                                            style={{ margin: 0 }}
                                          />
                                          Italic
                                        </label>
                                      </div>
                                    </div>
                                    <div style={{ flex: '0 1 120px' }}>
                                      <SettingLabel>Input field padding (left):</SettingLabel>
                                      <SettingInput
                                        type="number"
                                        value={field.inputPaddingLeft || 16}
                                        onChange={(e) => updateField(field.id, { inputPaddingLeft: parseInt(e.target.value) || 16 })}
                                        placeholder="16"
                                        style={{ fontSize: '12px', padding: '4px 6px', marginTop: '4px', width: '100%' }}
                                      />
                                    </div>
                                  </div>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Minimum:</SettingLabel>
                                  <SettingInput
                                    type="number"
                                    value={Number.isFinite(field.min) ? field.min : 0}
                                    onChange={(e) => updateField(field.id, { min: parseFloat(e.target.value) })}
                                  />
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>Maximum:</SettingLabel>
                                  <SettingInput
                                    type="number"
                                    value={Number.isFinite(field.max) ? field.max : 10}
                                    onChange={(e) => updateField(field.id, { max: parseFloat(e.target.value) })}
                                  />
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>Step:</SettingLabel>
                                  <SettingInput
                                    type="number"
                                    value={Number.isFinite(field.step) ? field.step : 1}
                                    onChange={(e) => updateField(field.id, { step: parseFloat(e.target.value) })}
                                  />
                                </SettingGroup>
                              </>
                            )}
                            {field.type === 'matrix' && (
                              <>
                                <SettingGroup>
                                  <SettingLabel>Cell Type:</SettingLabel>
                                  <Select
                                    value={field.matrixCellType || 'radio'}
                                    onChange={(e) => updateField(field.id, { matrixCellType: e.target.value })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value="radio">Single choice (radio)</option>
                                    <option value="checkbox">Multiple choice (checkbox)</option>
                                    <option value="text">Text input</option>
                                    <option value="number">Number input</option>
                                  </Select>
                                </SettingGroup>

                                <SettingGroup>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                                    <input
                                      type="checkbox"
                                      checked={!!field.hideRowLabels}
                                      onChange={(e) => updateField(field.id, { hideRowLabels: e.target.checked })}
                                    />
                                    Hide row labels (only horizontal headings)
                                  </label>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Number of rows (used when rows are empty):</SettingLabel>
                                  <SettingInput
                                    type="number"
                                    min={1}
                                    value={field.matrixRowsCount ?? 10}
                                    onChange={(e) => updateField(field.id, { matrixRowsCount: parseInt(e.target.value || '0', 10) })}
                                  />
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Rows (one per line):</SettingLabel>
                                  <textarea
                                    value={(field.matrixRows || []).map(r => (typeof r === 'string' ? r : (r.label || r.key))).join('\n')}
                                    onChange={(e) => updateField(field.id, { matrixRows: e.target.value.split(/\r?\n/).filter(Boolean) })}
                                    placeholder="Row 1\nRow 2\nRow 3"
                                    style={{ width: '100%', minHeight: '90px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white' }}
                                  />
                                </SettingGroup>

                                <div style={{
                                  border: '1px dashed #d1d5db',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  background: '#f9fafb',
                                  marginBottom: '12px'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Per-row settings</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const count = field.matrixRowsCount ?? 10;
                                        const nextRows = Array.from({ length: count }, (_, i) => ({ key: `row_${i}`, label: `Row ${i + 1}` }));
                                        updateField(field.id, { matrixRows: nextRows });
                                      }}
                                      style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', background: 'white', cursor: 'pointer', fontSize: '12px' }}
                                    >
                                      Populate from count
                                    </button>
                                  </div>
                                  <div style={{ display: 'grid', gap: '8px' }}>
                                    {(field.matrixRows || []).map((r, idx) => (
                                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 36px', gap: '8px', alignItems: 'center' }}>
                                        <input
                                          type="text"
                                          value={typeof r === 'string' ? r : (r.label || '')}
                                          onChange={(e) => {
                                            const next = [...(field.matrixRows || [])];
                                            next[idx] = typeof r === 'string' ? { key: `row_${idx}`, label: e.target.value } : { ...r, label: e.target.value };
                                            updateField(field.id, { matrixRows: next });
                                          }}
                                          placeholder={`Row ${idx+1}`}
                                          style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px', background: 'white' }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = (field.matrixRows || []).filter((_, i) => i !== idx);
                                            updateField(field.id, { matrixRows: next });
                                          }}
                                          title="Remove row"
                                          style={{ border: '1px solid #ef4444', color: '#ef4444', background: 'white', borderRadius: '6px', padding: '6px', cursor: 'pointer' }}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = [...(field.matrixRows || [])];
                                        next.push({ key: `row_${next.length}`, label: `Row ${next.length+1}` });
                                        updateField(field.id, { matrixRows: next });
                                      }}
                                      style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', background: 'white', cursor: 'pointer', marginTop: '4px', fontSize: '12px' }}
                                    >
                                      + Add row
                                    </button>
                                  </div>
                                </div>

                                <SettingGroup>
                                  <SettingLabel>Columns (one per line):</SettingLabel>
                                  <textarea
                                    value={(field.matrixColumns || []).map(c => (typeof c === 'string' ? c : (c.label || c.key))).join('\n')}
                                    onChange={(e) => updateField(field.id, { matrixColumns: e.target.value.split(/\r?\n/).filter(Boolean) })}
                                    placeholder="Column A\nColumn B\nColumn C"
                                    style={{ width: '100%', minHeight: '90px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white' }}
                                  />
                                </SettingGroup>

                                <div style={{
                                  border: '1px dashed #d1d5db',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  background: '#f9fafb',
                                  marginBottom: '12px'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Per-column settings</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const count = field.matrixColumnsCount ?? 5;
                                        const nextCols = Array.from({ length: count }, (_, i) => ({ key: `col_${i+1}`, label: `Column ${i+1}`, width: field.matrixCellWidth ?? 180, height: field.matrixCellHeight ?? 36 }));
                                        updateField(field.id, { matrixColumns: nextCols });
                                      }}
                                      style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', background: 'white', cursor: 'pointer' }}
                                    >
                                      Populate from count
                                    </button>
                                  </div>
                                  <div style={{ display: 'grid', gap: '8px' }}>
                                    {(field.matrixColumns || []).map((c, idx) => (
                                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 36px', gap: '8px', alignItems: 'center' }}>
                                        <input
                                          type="text"
                                          value={typeof c === 'string' ? c : (c.label || '')}
                                          onChange={(e) => {
                                            const next = [...(field.matrixColumns || [])];
                                            next[idx] = typeof c === 'string' ? { key: `col_${idx+1}`, label: e.target.value } : { ...c, label: e.target.value };
                                            updateField(field.id, { matrixColumns: next });
                                          }}
                                          placeholder={`Column ${idx+1}`}
                                          style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px', background: 'white' }}
                                        />
                                        <input
                                          type="number"
                                          min={60}
                                          value={typeof c === 'object' && c.width ? c.width : (field.matrixCellWidth ?? 180)}
                                          onChange={(e) => {
                                            const next = [...(field.matrixColumns || [])];
                                            const w = parseInt(e.target.value || '0', 10);
                                            next[idx] = typeof c === 'string' ? { key: `col_${idx+1}`, label: c, width: w } : { ...c, width: w };
                                            updateField(field.id, { matrixColumns: next });
                                          }}
                                          placeholder="Width"
                                          title="Width (px)"
                                          style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px', background: 'white' }}
                                        />
                                        <input
                                          type="number"
                                          min={24}
                                          value={typeof c === 'object' && c.height ? c.height : (field.matrixCellHeight ?? 36)}
                                          onChange={(e) => {
                                            const next = [...(field.matrixColumns || [])];
                                            const h = parseInt(e.target.value || '0', 10);
                                            next[idx] = typeof c === 'string' ? { key: `col_${idx+1}`, label: c, height: h } : { ...c, height: h };
                                            updateField(field.id, { matrixColumns: next });
                                          }}
                                          placeholder="Height"
                                          title="Height (px)"
                                          style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px', background: 'white' }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = (field.matrixColumns || []).filter((_, i) => i !== idx);
                                            updateField(field.id, { matrixColumns: next });
                                          }}
                                          title="Remove column"
                                          style={{ border: '1px solid #ef4444', color: '#ef4444', background: 'white', borderRadius: '6px', padding: '6px', cursor: 'pointer' }}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const next = [...(field.matrixColumns || [])];
                                        next.push({ key: `col_${next.length+1}`, label: `Column ${next.length+1}`, width: field.matrixCellWidth ?? 180, height: field.matrixCellHeight ?? 36 });
                                        updateField(field.id, { matrixColumns: next });
                                      }}
                                      style={{ border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', background: 'white', cursor: 'pointer', marginTop: '4px' }}
                                    >
                                      + Add column
                                    </button>
                                  </div>
                                </div>

                                <SettingGroup>
                                  <SettingLabel>Number of columns (used when columns are empty):</SettingLabel>
                                  <SettingInput
                                    type="number"
                                    min={1}
                                    value={field.matrixColumnsCount ?? 5}
                                    onChange={(e) => updateField(field.id, { matrixColumnsCount: parseInt(e.target.value || '0', 10) })}
                                  />
                                </SettingGroup>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                  <SettingGroup>
                                    <SettingLabel>Cell width (px):</SettingLabel>
                                    <SettingInput
                                      type="number"
                                      min={60}
                                      value={field.matrixCellWidth ?? 180}
                                      onChange={(e) => updateField(field.id, { matrixCellWidth: parseInt(e.target.value || '0', 10) })}
                                    />
                                  </SettingGroup>
                                  <SettingGroup>
                                    <SettingLabel>Cell height (px):</SettingLabel>
                                    <SettingInput
                                      type="number"
                                      min={24}
                                      value={field.matrixCellHeight ?? 36}
                                      onChange={(e) => updateField(field.id, { matrixCellHeight: parseInt(e.target.value || '0', 10) })}
                                    />
                                  </SettingGroup>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                  <SettingGroup>
                                    <SettingLabel>Row heading font size (px):</SettingLabel>
                                    <SettingInput
                                      type="number"
                                      min={8}
                                      max={24}
                                      value={field.matrixRowFontSize ?? 14}
                                      onChange={(e) => updateField(field.id, { matrixRowFontSize: parseInt(e.target.value || '14', 10) })}
                                    />
                                  </SettingGroup>
                                  <SettingGroup>
                                    <SettingLabel>Column heading font size (px):</SettingLabel>
                                    <SettingInput
                                      type="number"
                                      min={8}
                                      max={24}
                                      value={field.matrixColumnFontSize ?? 14}
                                      onChange={(e) => updateField(field.id, { matrixColumnFontSize: parseInt(e.target.value || '14', 10) })}
                                    />
                                  </SettingGroup>
                                </div>
                              </>
                            )}

                            {field.type === 'calculation' ? (
                              <>
                                <SettingGroup>
                                  <SettingLabel>Formula:</SettingLabel>
                                  <SettingInput
                                    value={field.formula || ''}
                                    onChange={(e) => updateField(field.id, { formula: e.target.value })}
                                    placeholder="e.g., {field1} + {field2} * 0.1"
                                    style={{ fontFamily: 'monospace' }}
                                  />
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>Decimal Places:</SettingLabel>
                                  <Select
                                    value={field.decimalPlaces || 2}
                                    onChange={(e) => updateField(field.id, { decimalPlaces: parseInt(e.target.value) })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value={0}>0 (No decimals)</option>
                                    <option value={1}>1 decimal place</option>
                                    <option value={2}>2 decimal places</option>
                                    <option value={3}>3 decimal places</option>
                                    <option value={4}>4 decimal places</option>
                                  </Select>
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>
                                    <div style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px',
                                      padding: '5px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      backgroundColor: field.currency ? '#dbeafe' : '#f9fafb'
                                    }}>
                                      <span style={{ fontSize: '12px' }}>
                                        {field.currency ? '✓' : '☐'} Format as Currency
                                      </span>
                                    </div>
                                  </SettingLabel>
                                </SettingGroup>
                              </>
                            ) : null}

                            {field.type === 'rating' ? (
                              <>
                                <SettingGroup>
                                  <SettingLabel>Font Family:</SettingLabel>
                                  <Select
                                    value={field.fontFamily || 'default'}
                                    onChange={(e) => updateField(field.id, { fontFamily: e.target.value })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value="default">Default</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Verdana">Verdana</option>
                                    <option value="Courier New">Courier New</option>
                                    <option value="Impact">Impact</option>
                                    <option value="Comic Sans MS">Comic Sans MS</option>
                                    <option value="Trebuchet MS">Trebuchet MS</option>
                                    <option value="Palatino">Palatino</option>
                                    <option value="Garamond">Garamond</option>
                                  </Select>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Text Formatting:</SettingLabel>
                                  <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                                      <input
                                        type="checkbox"
                                        checked={field.bold || false}
                                        onChange={(e) => updateField(field.id, { bold: e.target.checked })}
                                        style={{ margin: 0 }}
                                      />
                                      Bold
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                                      <input
                                        type="checkbox"
                                        checked={field.italic || false}
                                        onChange={(e) => updateField(field.id, { italic: e.target.checked })}
                                        style={{ margin: 0 }}
                                      />
                                      Italic
                                    </label>
                                  </div>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Input Field Padding:</SettingLabel>
                                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', display: 'block' }}>Left Padding</label>
                                      <SettingInput
                                        type="number"
                                        value={field.inputPaddingLeft || 16}
                                        onChange={(e) => updateField(field.id, { inputPaddingLeft: parseInt(e.target.value) || 16 })}
                                        placeholder="16"
                                        style={{ fontSize: '12px', padding: '4px 6px' }}
                                      />
                                    </div>
                                  </div>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Icon Type:</SettingLabel>
                                  <Select
                                    value={field.ratingIcon || 'star'}
                                    onChange={(e) => updateField(field.id, { ratingIcon: e.target.value })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value="star">⭐ Star</option>
                                    <option value="heart">❤️ Heart</option>
                                    <option value="thumbs">👍 Thumbs Up</option>
                                    <option value="face">😊 Face</option>
                                    <option value="fire">🔥 Fire</option>
                                    <option value="check">✅ Check</option>
                                    <option value="emotion">😢😐😊😄😍 Emotion Faces</option>
                                  </Select>
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>⭐ Maximum Rating:</SettingLabel>
                                  <SettingInput
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={field.maxRating || 5}
                                    onChange={(e) => {
                                      const newMax = parseInt(e.target.value) || 5;
                                      const oldLabels = field.ratingLabels || ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
                                      // Adjust labels array to match the new max rating count
                                      let newLabels = [...oldLabels];
                                      if (newMax > oldLabels.length) {
                                        for (let i = oldLabels.length; i < newMax; i++) {
                                          newLabels.push(`Rating ${i + 1}`);
                                        }
                                      } else if (newMax < oldLabels.length) {
                                        newLabels = newLabels.slice(0, newMax);
                                      }
                                      updateField(field.id, { maxRating: newMax, ratingLabels: newLabels });
                                    }}
                                  />
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>📝 Rating Labels:</SettingLabel>
                                  <div style={{ marginTop: '8px' }}>
                                    {(field.ratingLabels || ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent']).map((label, index) => (
                                      <div key={index} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px', 
                                        marginBottom: '6px' 
                                      }}>
                                        <span style={{ 
                                          fontSize: '12px', 
                                          color: '#6b7280',
                                          minWidth: '20px'
                                        }}>
                                          {index + 1}:
                                        </span>
                                        <input
                                          type="text"
                                          value={label}
                                          onChange={(e) => {
                                            const newLabels = [...(field.ratingLabels || ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'])];
                                            newLabels[index] = e.target.value;
                                            updateField(field.id, { ratingLabels: newLabels });
                                          }}
                                          style={{
                                            flex: 1,
                                            padding: '6px 8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            fontSize: '12px'
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>
                                    <div style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                      padding: '8px',
                                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                      borderRadius: '6px',
                                      border: '1px solid rgba(0, 0, 0, 0.1)'
                                    }}>
                                      Tip: Rating labels help users understand what each star represents
                                    </div>
                                  </SettingLabel>
                                </SettingGroup>
                              </>
                            ) : null}

                            {field.type === 'file' ? (
                              <>
                                <SettingGroup>
                                  <SettingLabel>📎 Accepted File Types:</SettingLabel>
                                  <div style={{ marginTop: '8px' }}>
                                    <div style={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
                                      gap: '6px',
                                      marginBottom: '8px'
                                    }}>
                                      {['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar', 'mp4', 'mov', 'avi', 'mkv', 'webm'].map((fileType) => (
                                        <label key={fileType} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '6px 8px',
                                          border: '1px solid rgba(0, 0, 0, 0.2)',
                                          borderRadius: '6px',
                                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: '500'
                                        }}>
                                          <input
                                            type="checkbox"
                                            checked={(field.acceptedFileTypes || []).includes(fileType)}
                                            onChange={(e) => {
                                              const currentTypes = field.acceptedFileTypes || [];
                                              const newTypes = e.target.checked
                                                ? [...currentTypes, fileType]
                                                : currentTypes.filter(type => type !== fileType);
                                              updateField(field.id, { acceptedFileTypes: newTypes });
                                            }}
                                            style={{ margin: 0, accentColor: 'black' }}
                                          />
                                          {fileType.toUpperCase()}
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>📏 Maximum File Size (MB):</SettingLabel>
                                  <Select
                                    value={field.maxFileSize || 10}
                                    onChange={(e) => updateField(field.id, { maxFileSize: parseInt(e.target.value) })}
                                    style={{ background: 'white', marginTop: '4px' }}
                                  >
                                    <option value={1}>1 MB</option>
                                    <option value={5}>5 MB</option>
                                    <option value={10}>10 MB</option>
                                    <option value={25}>25 MB</option>
                                    <option value={50}>50 MB</option>
                                    <option value={100}>100 MB</option>
                                  </Select>
                                </SettingGroup>
                                <SettingGroup>
                                  <SettingLabel>
                                    <div style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px',
                                      padding: '8px 12px',
                                      border: '2px solid rgba(0, 0, 0, 0.1)',
                                      borderRadius: '8px',
                                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                      <input
                                        type="checkbox"
                                        checked={field.multipleFiles || false}
                                        onChange={(e) => updateField(field.id, { multipleFiles: e.target.checked })}
                                        style={{ 
                                          width: '16px',
                                          height: '16px',
                                          accentColor: 'black',
                                          cursor: 'pointer'
                                        }}
                                      />
                                      <span style={{ 
                                        fontSize: '13px', 
                                        color: '#374151',
                                        fontWeight: '500'
                                      }}>
                                        📁 Allow Multiple Files
                                      </span>
                                    </div>
                                  </SettingLabel>
                                </SettingGroup>
                              </>
                            ) : null}

                            {field.type === 'contact' && (
                              <>
                                <SettingGroup>
                                  <SettingLabel>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      Contact Information Fields
                                      <span style={{ 
                                        fontSize: '12px', 
                                        backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                                        color: 'black',
                                        padding: '2px 6px',
                                        borderRadius: '8px'
                                      }}>
                                        {field.contactFields?.length || 0} fields
                                      </span>
                                    </div>
                                  </SettingLabel>
                                  <div style={{ 
                                    marginTop: '12px',
                                    padding: '16px',
                                    border: '1px dashed rgba(0, 0, 0, 0.3)',
                                    borderRadius: '12px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                  }}>
                                    {(() => {
                                        const fieldTypeLabels = {
                                          'text': 'Text',
                                          'email': 'Email',
                                          'textarea': 'Text Area',
                                          'number': 'Number',
                                          'date': 'Date',
                                          'radio': 'Gender'
                                        };
                                      return (
                                        <>
                                          <div style={{ marginBottom: '16px' }}>
                                            <div style={{ 
                                              fontSize: '13px', 
                                              color: '#6b7280', 
                                              marginBottom: '8px',
                                              fontWeight: '500'
                                            }}>
                                              Add field types:
                                            </div>
                                            <div style={{ 
                                              display: 'grid', 
                                              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                                              gap: '8px'
                                            }}>
                                              {['text', 'email', 'textarea', 'number', 'date', 'radio'].map((fieldType) => (
                                                <button
                                                  key={fieldType}
                                                  onClick={() => addContactField(field.id, fieldType)}
                                                  style={{
                                                    padding: '10px 12px',
                                                    border: '2px solid rgba(0, 0, 0, 0.2)',
                                                    borderRadius: '10px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    color: 'black',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    justifyContent: 'center'
                                                  }}
                                                  onMouseOver={(e) => {
                                                    e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                                                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.4)';
                                                    e.target.style.transform = 'translateY(-2px)';
                                                  }}
                                                  onMouseOut={(e) => {
                                                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                                                    e.target.style.transform = 'translateY(0)';
                                                  }}
                                                >
                                                  <span style={{ fontSize: '14px' }}>
                                                    {fieldType === 'text' && '📝'}
                                                    {fieldType === 'email' && '📧'}
                                                    {fieldType === 'textarea' && '📄'}
                                                    {fieldType === 'number' && '🔢'}
                                                    {fieldType === 'date' && '📅'}
                                                    {fieldType === 'radio' && '⚪'}
                                                  </span>
                                                  {fieldTypeLabels[fieldType]}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                          
                                          {field.contactFields?.map((contactField, index) => (
                                      <div key={contactField.id} style={{
                                        border: '2px solid rgba(0, 0, 0, 0.1)',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        marginBottom: '12px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.2s ease',
                                        position: 'relative'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '12px' }}>
                                          <button
                                            onClick={() => removeContactField(field.id, contactField.id)}
                                            style={{
                                              padding: '6px 12px',
                                              border: '2px solid #ef4444',
                                              borderRadius: '8px',
                                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                              color: '#ef4444',
                                              fontSize: '12px',
                                              fontWeight: '600',
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => {
                                              e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                                              e.target.style.transform = 'scale(1.05)';
                                            }}
                                            onMouseOut={(e) => {
                                              e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                              e.target.style.transform = 'scale(1)';
                                            }}
                                          >
                                            🗑️ Remove
                                          </button>
                                        </div>
                                        
                                        <div style={{ marginBottom: '12px' }}>
                                          <input
                                            type="text"
                                            value={contactField.label}
                                            onChange={(e) => updateContactField(field.id, contactField.id, { label: e.target.value })}
                                            placeholder="Type your question here..."
                                            style={{
                                              width: '100%',
                                              padding: '10px 12px',
                                              border: '2px solid rgba(0, 0, 0, 0.2)',
                                              borderRadius: '8px',
                                              fontSize: '13px',
                                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                              transition: 'all 0.2s ease'
                                            }}
                                            onFocus={(e) => {
                                              e.target.style.borderColor = 'rgba(0, 0, 0, 0.5)';
                                              e.target.style.backgroundColor = '#ffffff';
                                            }}
                                            onBlur={(e) => {
                                              e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                                              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                            }}
                                          />
                                        </div>
                                        
                                        {contactField.type === 'radio' && contactField.options && (
                                          <div style={{ 
                                            marginBottom: '12px',
                                            padding: '12px',
                                            border: '2px dashed rgba(0, 0, 0, 0.2)',
                                            borderRadius: '10px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                          }}>
                                            <label style={{ 
                                              fontSize: '13px', 
                                              color: '#374151', 
                                              display: 'block', 
                                              marginBottom: '8px',
                                              fontWeight: '500'
                                            }}>
                                              ⚪ Radio Button Options:
                                            </label>
                                            {contactField.options.map((option, optionIndex) => (
                                              <div key={optionIndex} style={{ 
                                                display: 'flex', 
                                                gap: '10px', 
                                                marginBottom: '8px', 
                                                alignItems: 'center',
                                                padding: '8px',
                                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                                borderRadius: '8px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)'
                                              }}>
                                                <span style={{ fontSize: '14px', color: '#6b7280' }}>⚪</span>
                                                <input
                                                  type="text"
                                                  value={option}
                                                  onChange={(e) => {
                                                    const newOptions = [...contactField.options];
                                                    newOptions[optionIndex] = e.target.value;
                                                    updateContactField(field.id, contactField.id, { options: newOptions });
                                                  }}
                                                  placeholder="Enter option text"
                                                  style={{
                                                    flex: 1,
                                                    padding: '8px 10px',
                                                    border: '2px solid rgba(0, 0, 0, 0.2)',
                                                    borderRadius: '6px',
                                                    fontSize: '13px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    transition: 'all 0.2s ease'
                                                  }}
                                                  onFocus={(e) => {
                                                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.5)';
                                                    e.target.style.backgroundColor = '#ffffff';
                                                  }}
                                                  onBlur={(e) => {
                                                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                                                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                                  }}
                                                />
                                                <button
                                                  onClick={() => {
                                                    const newOptions = contactField.options.filter((_, idx) => idx !== optionIndex);
                                                    updateContactField(field.id, contactField.id, { options: newOptions });
                                                  }}
                                                  style={{
                                                    padding: '6px 10px',
                                                    border: '2px solid #ef4444',
                                                    borderRadius: '6px',
                                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                    color: '#ef4444',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                  }}
                                                  onMouseOver={(e) => {
                                                    e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                                                    e.target.style.transform = 'scale(1.05)';
                                                  }}
                                                  onMouseOut={(e) => {
                                                    e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                                    e.target.style.transform = 'scale(1)';
                                                  }}
                                                >
                                                  🗑️
                                                </button>
                                              </div>
                                            ))}
                                            <button
                                              onClick={() => {
                                                const newOptions = [...(contactField.options || []), `Option ${(contactField.options?.length || 0) + 1}`];
                                                updateContactField(field.id, contactField.id, { options: newOptions });
                                              }}
                                              style={{
                                                padding: '8px 12px',
                                                border: '2px solid black',
                                                borderRadius: '8px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                color: 'black',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                              }}
                                              onMouseOver={(e) => {
                                                e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
                                                e.target.style.transform = 'translateY(-2px)';
                                              }}
                                              onMouseOut={(e) => {
                                                e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                                                e.target.style.transform = 'translateY(0)';
                                              }}
                                            >
                                              ➕ Add Option
                                            </button>
                                          </div>
                                        )}
                                        
                                        <div style={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: '10px',
                                          padding: '8px 12px',
                                          border: '2px solid rgba(0, 0, 0, 0.1)',
                                          borderRadius: '8px',
                                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                        }}>
                                          <input
                                            type="checkbox"
                                            checked={contactField.required || false}
                                            onChange={(e) => updateContactField(field.id, contactField.id, { required: e.target.checked })}
                                            style={{ 
                                              width: '16px',
                                              height: '16px',
                                              accentColor: 'black',
                                              cursor: 'pointer'
                                            }}
                                          />
                                          <span style={{ 
                                            fontSize: '13px', 
                                            color: '#374151',
                                            fontWeight: '500'
                                          }}>
                                            ⭐ Required field
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </SettingGroup>
                                
                                <SettingGroup>
                                  <SettingLabel>Field Size:</SettingLabel>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {[
                                      { value: 'small', label: 'Small', icon: '📏' },
                                      { value: 'medium', label: 'Medium', icon: '📐' },
                                      { value: 'large', label: 'Large', icon: '📊' }
                                    ].map((size) => (
                                      <button
                                        key={size.value}
                                        onClick={() => updateField(field.id, { fieldSize: size.value })}
                                        style={{
                                          padding: '8px 12px',
                                          border: `2px solid ${field.fieldSize === size.value ? '#000' : '#e5e7eb'}`,
                                          borderRadius: '8px',
                                          backgroundColor: field.fieldSize === size.value ? '#000' : '#ffffff',
                                          color: field.fieldSize === size.value ? '#ffffff' : '#374151',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}
                                        onMouseOver={(e) => {
                                          if (field.fieldSize !== size.value) {
                                            e.target.style.borderColor = '#9ca3af';
                                            e.target.style.backgroundColor = '#f9fafb';
                                          }
                                        }}
                                        onMouseOut={(e) => {
                                          if (field.fieldSize !== size.value) {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.backgroundColor = '#ffffff';
                                          }
                                        }}
                                      >
                                        <span>{size.icon}</span>
                                        {size.label}
                                      </button>
                                    ))}
                                  </div>
                                </SettingGroup>
                              </>
                            )}

                            {field.type === 'repeatable' && (
                              <>
                                <SettingGroup>
                                  <SettingLabel>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      Loop Fields
                                      <span style={{ 
                                        fontSize: '12px', 
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                                        color: '#1e40af',
                                        padding: '2px 6px',
                                        borderRadius: '8px'
                                      }}>
                                        {field.repeatableFields?.length || 0} fields
                                      </span>
                                    </div>
                                  </SettingLabel>
                                  <div style={{ 
                                    marginTop: '12px',
                                    padding: '16px',
                                    border: '1px dashed rgba(59, 130, 246, 0.3)',
                                    borderRadius: '12px',
                                    backgroundColor: 'rgba(59, 130, 246, 0.02)'
                                  }}>
                                    <p style={{ fontSize: '12px', color: '#374151', marginBottom: '12px' }}>
                                      Add the questions that should repeat for each new entry.
                                    </p>
                                    <div style={{ marginBottom: '16px' }}>
                                      <div style={{ 
                                        fontSize: '13px', 
                                        color: '#6b7280', 
                                        marginBottom: '8px',
                                        fontWeight: '500'
                                      }}>
                                        Add a field:
                                      </div>
                                      <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                                        gap: '8px'
                                      }}>
                                        {['text', 'textarea', 'number', 'email', 'date', 'checkbox', 'select', 'rating', 'file', 'mapLocation', 'heading', 'subheading'].map((fieldType) => {
                                          const fieldLabels = {
                                            'text': 'Text',
                                            'textarea': 'Text Area',
                                            'number': 'Number',
                                            'email': 'Email',
                                            'date': 'Date',
                                            'checkbox': 'Checkbox',
                                            'select': 'Dropdown',
                                            'rating': 'Rating',
                                            'file': 'File Upload',
                                            'mapLocation': 'Current location',
                                            'heading': 'Heading',
                                            'subheading': 'Subheading'
                                          };
                                          const fieldIcons = {
                                            'text': '📝',
                                            'textarea': '📄',
                                            'number': '🔢',
                                            'email': '📧',
                                            'date': '📅',
                                            'checkbox': '☑️',
                                            'select': '📋',
                                            'rating': '⭐',
                                            'file': '📎',
                                            'mapLocation': '📍',
                                            'heading': '📌',
                                            'subheading': '📋'
                                          };
                                          return (
                                            <button
                                              key={fieldType}
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                addRepeatableField(field.id, fieldType);
                                              }}
                                              style={{
                                                padding: '10px 12px',
                                                border: '2px solid rgba(59, 130, 246, 0.3)',
                                                borderRadius: '10px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                color: '#1e40af',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                justifyContent: 'center'
                                              }}
                                              onMouseOver={(e) => {
                                                e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                                e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                                                e.target.style.transform = 'translateY(-2px)';
                                              }}
                                              onMouseOut={(e) => {
                                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                                e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                                                e.target.style.transform = 'translateY(0)';
                                              }}
                                            >
                                              <span style={{ fontSize: '14px' }}>
                                                {fieldIcons[fieldType] || '📝'}
                                              </span>
                                              {fieldLabels[fieldType] || fieldType}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    
                                    <Droppable droppableId={`repeatable-${field.id}`} type={`repeatable-${field.id}`}>
                                      {(repeatableProvided) => (
                                        <div
                                          ref={repeatableProvided.innerRef}
                                          {...repeatableProvided.droppableProps}
                                        >
                                          {field.repeatableFields?.map((repeatableField, index) => (
                                            <Draggable key={repeatableField.id} draggableId={repeatableField.id} index={index}>
                                              {(repeatableItemProvided, repeatableSnapshot) => (
                                                <div
                                                  ref={repeatableItemProvided.innerRef}
                                                  {...repeatableItemProvided.draggableProps}
                                                  style={{
                                                    border: '2px solid rgba(59, 130, 246, 0.2)',
                                                    borderRadius: '12px',
                                                    padding: '16px',
                                                    marginBottom: '12px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    backdropFilter: 'blur(10px)',
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative',
                                                    boxShadow: repeatableSnapshot.isDragging ? '0 10px 20px rgba(59, 130, 246, 0.2)' : 'none',
                                                    ...repeatableItemProvided.draggableProps.style
                                                  }}
                                                >
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <div
                                                      {...repeatableItemProvided.dragHandleProps}
                                                      style={{
                                                        cursor: 'grab',
                                                        padding: '6px 10px',
                                                        borderRadius: '8px',
                                                        border: '2px solid rgba(59, 130, 246, 0.3)',
                                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                        color: '#1e40af',
                                                        fontWeight: 600,
                                                        fontSize: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                      }}
                                                    >
                                                      <span style={{ fontSize: '18px', lineHeight: 1 }}>⋮⋮</span>
                                                      Drag
                                                    </div>
                                                    <button
                                                      onClick={() => removeRepeatableField(field.id, repeatableField.id)}
                                                      style={{
                                                        padding: '6px 12px',
                                                        border: '2px solid #ef4444',
                                                        borderRadius: '8px',
                                                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                        color: '#ef4444',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease'
                                                      }}
                                                      onMouseOver={(e) => {
                                                        e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                                                        e.target.style.transform = 'scale(1.05)';
                                                      }}
                                                      onMouseOut={(e) => {
                                                        e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                                        e.target.style.transform = 'scale(1)';
                                                      }}
                                                    >
                                                      🗑️ Remove
                                                    </button>
                                                  </div>
                                                  
                                                  {repeatableField.type !== 'heading' && repeatableField.type !== 'subheading' && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                      <SettingInput
                                                        type="text"
                                                        value={repeatableField.label || ''}
                                                        onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { label: e.target.value })}
                                                        placeholder={repeatableField.type === 'rating' ? 'Provide rating question...' : 'Type your question here...'}
                                                        style={{
                                                          width: '100%',
                                                          padding: '10px 12px',
                                                          border: '2px solid rgba(59, 130, 246, 0.2)',
                                                          borderRadius: '8px',
                                                          fontSize: '13px',
                                                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                          transition: 'all 0.2s ease'
                                                        }}
                                                        onFocus={(e) => {
                                                          e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                                                          e.target.style.backgroundColor = '#ffffff';
                                                        }}
                                                        onBlur={(e) => {
                                                          e.target.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                                                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                  {repeatableField.type === 'rating' && (
                                                    <>
                                                      <SettingGroup>
                                                        <SettingLabel>Max Rating:</SettingLabel>
                                                        <SettingInput
                                                          type="number"
                                                          min="1"
                                                          max="10"
                                                          value={repeatableField.maxRating || 5}
                                                          onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { maxRating: parseInt(e.target.value) || 5 })}
                                                        />
                                                      </SettingGroup>
                                                      <SettingGroup>
                                                        <SettingLabel>Rating Labels (comma-separated):</SettingLabel>
                                                        <SettingInput
                                                          type="text"
                                                          value={repeatableField.ratingLabels?.join(', ') || ''}
                                                          onChange={(e) => {
                                                            const labels = e.target.value.split(',').map(l => l.trim()).filter(Boolean);
                                                            updateRepeatableField(field.id, repeatableField.id, { ratingLabels: labels });
                                                          }}
                                                          placeholder="Poor, Fair, Good, Very Good, Excellent"
                                                        />
                                                      </SettingGroup>
                                                    </>
                                                  )}

                                                  {(repeatableField.type === 'checkbox' || repeatableField.type === 'select') && (
                                                    <div style={{ 
                                                      marginTop: '16px',
                                                      padding: '12px',
                                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                                      borderRadius: '8px',
                                                      backgroundColor: 'rgba(59, 130, 246, 0.02)'
                                                    }}>
                                                      <label style={{ 
                                                        fontSize: '13px', 
                                                        color: '#374151', 
                                                        marginBottom: '12px', 
                                                        display: 'block', 
                                                        fontWeight: '600' 
                                                      }}>
                                                        Options:
                                                      </label>
                                                      {(repeatableField.options || []).map((option, optionIndex) => (
                                                        <div 
                                                          key={optionIndex}
                                                          style={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: '8px', 
                                                            marginBottom: '8px',
                                                            padding: '8px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '6px',
                                                            border: '1px solid #e5e7eb'
                                                          }}
                                                        >
                                                          <input
                                                            type="text"
                                                            value={option}
                                                            onChange={(e) => updateRepeatableFieldOption(field.id, repeatableField.id, optionIndex, e.target.value)}
                                                            placeholder={`Choice ${optionIndex + 1}`}
                                                            style={{
                                                              flex: 1,
                                                              padding: '6px 8px',
                                                              border: '1px solid #d1d5db',
                                                              borderRadius: '4px',
                                                              fontSize: '12px',
                                                              backgroundColor: '#f9fafb'
                                                            }}
                                                          />
                                                          <button
                                                            type="button"
                                                            onClick={() => removeRepeatableFieldOption(field.id, repeatableField.id, optionIndex)}
                                                            style={{
                                                              padding: '6px 10px',
                                                              border: '1px solid #ef4444',
                                                              borderRadius: '4px',
                                                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                              color: '#ef4444',
                                                              fontSize: '12px',
                                                              fontWeight: '600',
                                                              cursor: 'pointer',
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              justifyContent: 'center'
                                                            }}
                                                            title="Remove option"
                                                          >
                                                            <FiX />
                                                          </button>
                                                        </div>
                                                      ))}
                                                      <button
                                                        type="button"
                                                        onClick={() => addRepeatableFieldOption(field.id, repeatableField.id)}
                                                        style={{
                                                          width: '100%',
                                                          padding: '8px 12px',
                                                          border: '2px dashed rgba(59, 130, 246, 0.3)',
                                                          borderRadius: '6px',
                                                          backgroundColor: 'rgba(59, 130, 246, 0.05)',
                                                          color: '#1e40af',
                                                          fontSize: '12px',
                                                          fontWeight: '600',
                                                          cursor: 'pointer',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          gap: '6px',
                                                          transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseOver={(e) => {
                                                          e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                                          e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                                                        }}
                                                        onMouseOut={(e) => {
                                                          e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                                                          e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                                                        }}
                                                        title="Add new choice"
                                                      >
                                                        <FiPlus />
                                                        Add Choice
                                                      </button>
                                                      <div style={{ marginTop: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
                                                        <label style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', display: 'block', fontWeight: '500' }}>
                                                          Bulk Add Options:
                                                        </label>
                                                        <textarea
                                                          placeholder="Paste options (one per line or comma-separated)"
                                                          onPaste={(e) => {
                                                            e.preventDefault();
                                                            const pastedText = e.clipboardData.getData('text');
                                                            bulkAddRepeatableFieldOptions(field.id, repeatableField.id, pastedText);
                                                            e.target.value = '';
                                                          }}
                                                          onChange={(e) => {
                                                            if (e.target.value.includes('\n') || e.target.value.includes(',')) {
                                                              bulkAddRepeatableFieldOptions(field.id, repeatableField.id, e.target.value);
                                                              e.target.value = '';
                                                            }
                                                          }}
                                                          onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && e.ctrlKey) {
                                                              e.preventDefault();
                                                              bulkAddRepeatableFieldOptions(field.id, repeatableField.id, e.target.value);
                                                              e.target.value = '';
                                                            }
                                                          }}
                                                          style={{
                                                            width: '100%',
                                                            minHeight: '60px',
                                                            padding: '6px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '4px',
                                                            fontSize: '11px',
                                                            fontFamily: 'inherit',
                                                            resize: 'vertical'
                                                          }}
                                                        />
                                                        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
                                                          Paste options separated by newlines or commas
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}

                                                  {repeatableField.type === 'checkbox' && (
                                                    <div style={{ marginTop: '12px' }}>
                                                      <SettingGroup>
                                                        <SettingLabel>Display Layout:</SettingLabel>
                                                        <Select
                                                          value={repeatableField.checkboxLayout || 'vertical'}
                                                          onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { checkboxLayout: e.target.value })}
                                                          style={{ background: 'white', marginTop: '4px', width: '100%' }}
                                                        >
                                                          <option value="vertical">Line by Line (Vertical)</option>
                                                          <option value="horizontal">In a Row (Horizontal)</option>
                                                        </Select>
                                                      </SettingGroup>
                                                    </div>
                                                  )}

                                                  {repeatableField.type === 'file' ? (
                                                    <>
                                                      <SettingGroup>
                                                        <SettingLabel>📎 Accepted File Types:</SettingLabel>
                                                        <div style={{ marginTop: '8px' }}>
                                                          <div style={{ 
                                                            display: 'grid', 
                                                            gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
                                                            gap: '6px',
                                                            marginBottom: '8px'
                                                          }}>
                                                            {['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar', 'mp4', 'mov', 'avi', 'mkv', 'webm'].map((fileType) => (
                                                              <label key={fileType}
                                                                style={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: '6px',
                                                                  padding: '6px 10px',
                                                                  border: '2px solid rgba(59, 130, 246, 0.2)',
                                                                  borderRadius: '6px',
                                                                  backgroundColor: (repeatableField.acceptedFileTypes || []).includes(fileType) ? 'rgba(59, 130, 246, 0.1)' : 'white',
                                                                  cursor: 'pointer',
                                                                  fontSize: '12px',
                                                                  fontWeight: '500',
                                                                  transition: 'all 0.2s ease'
                                                                }}
                                                              >
                                                                <input
                                                                  type="checkbox"
                                                                  checked={(repeatableField.acceptedFileTypes || []).includes(fileType)}
                                                                  onChange={(e) => {
                                                                    const currentTypes = repeatableField.acceptedFileTypes || [];
                                                                    const newTypes = e.target.checked
                                                                      ? [...currentTypes, fileType]
                                                                      : currentTypes.filter(t => t !== fileType);
                                                                    updateRepeatableField(field.id, repeatableField.id, { acceptedFileTypes: newTypes });
                                                                  }}
                                                                  style={{ 
                                                                    width: '14px',
                                                                    height: '14px',
                                                                    accentColor: '#3b82f6',
                                                                    cursor: 'pointer'
                                                                  }}
                                                                />
                                                                {fileType.toUpperCase()}
                                                              </label>
                                                            ))}
                                                          </div>
                                                        </div>
                                                      </SettingGroup>
                                                      <SettingGroup>
                                                        <SettingLabel>📦 Max File Size (MB):</SettingLabel>
                                                        <SettingInput
                                                          type="number"
                                                          min="1"
                                                          max="100"
                                                          value={repeatableField.maxFileSize || 10}
                                                          onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { maxFileSize: parseInt(e.target.value) || 10 })}
                                                          style={{ background: 'white', marginTop: '4px' }}
                                                        />
                                                      </SettingGroup>
                                                      <SettingGroup>
                                                        <SettingLabel>Multiple Files:</SettingLabel>
                                                        <div style={{ 
                                                          display: 'flex', 
                                                          alignItems: 'center', 
                                                          gap: '10px',
                                                          padding: '8px 12px',
                                                          border: '2px solid rgba(59, 130, 246, 0.1)',
                                                          borderRadius: '8px',
                                                          backgroundColor: 'rgba(59, 130, 246, 0.02)',
                                                          marginTop: '4px'
                                                        }}>
                                                          <input
                                                            type="checkbox"
                                                            checked={repeatableField.multipleFiles || false}
                                                            onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { multipleFiles: e.target.checked })}
                                                            style={{ 
                                                              width: '16px',
                                                              height: '16px',
                                                              accentColor: '#3b82f6',
                                                              cursor: 'pointer'
                                                            }}
                                                          />
                                                          <span style={{ 
                                                            fontSize: '13px', 
                                                            color: '#374151',
                                                            fontWeight: '500'
                                                          }}>
                                                            Allow users to upload multiple files
                                                          </span>
                                                        </div>
                                                      </SettingGroup>
                                                    </>
                                                  ) : null}

                                                  {(repeatableField.type === 'heading' || repeatableField.type === 'subheading') && (
                                                    <>
                                                      <SettingGroup>
                                                        <SettingLabel>{repeatableField.type === 'heading' ? 'Heading Text:' : 'Subheading Text:'}</SettingLabel>
                                                        <SettingInput
                                                          type="text"
                                                          value={repeatableField.label || ''}
                                                          onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { label: e.target.value })}
                                                          placeholder={repeatableField.type === 'heading' ? 'Enter heading text...' : 'Enter subheading text...'}
                                                        />
                                                      </SettingGroup>
                                                      <SettingGroup>
                                                        <SettingLabel>Heading Level:</SettingLabel>
                                                        <Select
                                                          value={repeatableField.level || (repeatableField.type === 'heading' ? 2 : 4)}
                                                          onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { level: parseInt(e.target.value) })}
                                                          style={{ background: 'white', marginTop: '4px' }}
                                                        >
                                                          <option value={1}>H1 - Largest</option>
                                                          <option value={2}>H2 - Large</option>
                                                          <option value={3}>H3 - Medium</option>
                                                          <option value={4}>H4 - Small</option>
                                                          <option value={5}>H5 - Smaller</option>
                                                          <option value={6}>H6 - Smallest</option>
                                                        </Select>
                                                      </SettingGroup>
                                                    </>
                                                  )}
                                                  
                                                  {repeatableField.type !== 'heading' && repeatableField.type !== 'subheading' && repeatableField.type !== 'file' && (
                                                    <div style={{ 
                                                      display: 'flex', 
                                                      alignItems: 'center', 
                                                      gap: '10px',
                                                      padding: '8px 12px',
                                                      border: '2px solid rgba(59, 130, 246, 0.1)',
                                                      borderRadius: '8px',
                                                      backgroundColor: 'rgba(59, 130, 246, 0.02)'
                                                    }}>
                                                      <input
                                                        type="checkbox"
                                                        checked={repeatableField.required || false}
                                                        onChange={(e) => updateRepeatableField(field.id, repeatableField.id, { required: e.target.checked })}
                                                        style={{ 
                                                          width: '16px',
                                                          height: '16px',
                                                          accentColor: '#3b82f6',
                                                          cursor: 'pointer'
                                                        }}
                                                      />
                                                      <span style={{ 
                                                        fontSize: '13px', 
                                                        color: '#374151',
                                                        fontWeight: '500'
                                                      }}>
                                                        ⭐ Required field
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </Draggable>
                                          ))}
                                          {repeatableProvided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  </div>
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Repeat Prompt:</SettingLabel>
                                  <SettingInput
                                    type="text"
                                    value={field.continueQuestion || ''}
                                    onChange={(e) => updateField(field.id, { continueQuestion: e.target.value })}
                                    placeholder="Add another entry?"
                                  />
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Continue Button Text:</SettingLabel>
                                  <SettingInput
                                    type="text"
                                    value={field.continueYesLabel || ''}
                                    onChange={(e) => updateField(field.id, { continueYesLabel: e.target.value })}
                                    placeholder="Yes, add another"
                                  />
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Finish Button Text:</SettingLabel>
                                  <SettingInput
                                    type="text"
                                    value={field.continueNoLabel || ''}
                                    onChange={(e) => updateField(field.id, { continueNoLabel: e.target.value })}
                                    placeholder="No, I'm done"
                                  />
                                </SettingGroup>

                                <SettingGroup>
                                  <SettingLabel>Display Options:</SettingLabel>
                                  <div style={{ marginTop: '8px' }}>
                                    <label style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '8px', 
                                      fontSize: '13px', 
                                      color: '#374151',
                                      cursor: 'pointer'
                                    }}>
                                      <input
                                        type="checkbox"
                                        checked={field.showContinueButton !== false}
                                        onChange={(e) => updateField(field.id, { showContinueButton: e.target.checked })}
                                        style={{ 
                                          width: '16px',
                                          height: '16px',
                                          accentColor: '#3b82f6',
                                          cursor: 'pointer'
                                        }}
                                      />
                                      <span>Show "Add Another" button</span>
                                    </label>
                                    <div style={{ 
                                      fontSize: '11px', 
                                      color: '#6b7280', 
                                      marginTop: '4px',
                                      marginLeft: '24px'
                                    }}>
                                      When unchecked, users can add entries without the continue prompt
                                    </div>
                                  </div>
                                </SettingGroup>
                              </>
                            )}
                          </FieldSettingsContainer>

                          <FieldQuickActions>
                            <QuickActionButtonSmall onClick={openFieldSelector}>
                              <FiPlus />
                              Add Question
                            </QuickActionButtonSmall>
                            <QuickActionButtonSmall onClick={openFieldSelector}>
                              <FiType />
                              Add Section Header
                            </QuickActionButtonSmall>
                            <QuickActionButtonSmall onClick={openFieldSelector}>
                              <FiUser />
                              Add Contact Block
                            </QuickActionButtonSmall>
                          </FieldQuickActions>

                        </FieldItem>
                      )}
                    </Draggable>
                    );
                  });
                  })()}
                  {provided.placeholder}

                  {/* Persistent bottom create section button (moves downward as list grows) */}
                  {form.form_structure.length > 0 && (
                    <div style={{
                      gridColumn: 'span 12', // Force full width in grid
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '16px 0',
                    }}>
                      <button
                        type="button"
                        onClick={() => {
                          addField('section', form.form_structure.length);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 16px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          color: '#374151',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.borderColor = '#9ca3af';
                          e.target.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.backgroundColor = 'white';
                        }}
                      >
                        + Create new section
                      </button>
                    </div>
                  )}

                  {/* Bottom quick action buttons removed */}
                  </FieldList>
                )}
              </Droppable>
              </div>
            )}
            </div>
            )}
          </DragDropContext>

          {/* Save Form Button at Bottom */}
        {currentStep === 2 && form.form_structure && form.form_structure.length > 0 && (
            <div style={{ 
              marginTop: '40px', 
              padding: '24px', 
              background: 'rgba(0, 0, 0, 0.05)', 
              borderRadius: '12px',
              border: '2px dashed rgba(0, 0, 0, 0.2)',
              textAlign: 'center'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '12px', 
                color: '#1f2937' 
              }}>
                Ready to Save Your Form?
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#6b7280', 
                marginBottom: '20px' 
              }}>
                Your form has {form.form_structure.length} field{form.form_structure.length !== 1 ? 's' : ''}. 
                Save your form, preview it, or share it with others.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <QuickActionButton onClick={saveForm} disabled={saving} style={{
                  background: 'black',
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }}>
                  <FiSave />
                  {saving ? 'Saving...' : (formId ? 'Update Form' : 'Save Form')}
                </QuickActionButton>
                <QuickActionButton onClick={() => {
                  // Store form data in localStorage for preview
                  const formDataWithSettings = {
                    ...form,
                    enable_multi_step: form.enableMultiStep || false,
                    group_sections_with_questions: form.groupSectionsWithQuestions || false,
                    label_font_size: form.labelFontSize || '14px'
                  };
                  localStorage.setItem('previewForm', JSON.stringify(formDataWithSettings));
                  console.log('FormBuilder: Saved preview data to localStorage:', formDataWithSettings);
                  console.log('FormBuilder: localStorage keys after save:', Object.keys(localStorage));
                  console.log('FormBuilder: Verifying saved data:', localStorage.getItem('previewForm')?.substring(0, 100) + '...');
                  window.open('/preview/preview', '_blank');
                }}>
                  <FiEye />
                  Preview Form
                </QuickActionButton>
                <QuickActionButton onClick={openShareModal}>
                  <FiShare2 />
                  Share Publicly
                </QuickActionButton>
                <QuickActionButton onClick={openUserShareModal}>
                  <FiUsers />
                  Share with Users
                </QuickActionButton>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                Preview & Test
              </h3>
              <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px' }}>
                Your form is ready! Preview it, save it, or share it with others.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <QuickActionButton onClick={saveForm} disabled={saving} style={{
                  background: 'black',
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }}>
                  <FiSave />
                  {saving ? 'Saving...' : (formId ? 'Update Form' : 'Save Form')}
                </QuickActionButton>
                <QuickActionButton onClick={() => {
                  // Store form data in localStorage for preview
                  const formDataWithSettings = {
                    ...form,
                    enable_multi_step: form.enableMultiStep || false,
                    group_sections_with_questions: form.groupSectionsWithQuestions || false,
                    label_font_size: form.labelFontSize || '14px'
                  };
                  localStorage.setItem('previewForm', JSON.stringify(formDataWithSettings));
                  console.log('FormBuilder: Saved preview data to localStorage:', formDataWithSettings);
                  console.log('FormBuilder: localStorage keys after save:', Object.keys(localStorage));
                  console.log('FormBuilder: Verifying saved data:', localStorage.getItem('previewForm')?.substring(0, 100) + '...');
                  window.open('/preview/preview', '_blank');
                }}>
                  <FiEye />
                  Preview Form
                </QuickActionButton>
                <QuickActionButton onClick={openShareModal}>
                  <FiShare2 />
                  Share Publicly
                </QuickActionButton>
                <QuickActionButton onClick={openUserShareModal}>
                  <FiUsers />
                  Share with Users
                </QuickActionButton>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px', color: '#1f2937' }}>
                Save & Share
              </h3>
              <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px' }}>
                Ready to make your form live? Save it, preview it, and share with your team or the public.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <QuickActionButton onClick={saveForm} disabled={saving} style={{
                  background: 'black',
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }}>
                  <FiSave />
                  {saving ? 'Saving...' : (formId ? 'Update Form' : 'Save Form')}
                </QuickActionButton>
                <QuickActionButton onClick={() => {
                  // Store form data in localStorage for preview
                  const formDataWithSettings = {
                    ...form,
                    enable_multi_step: form.enableMultiStep || false,
                    group_sections_with_questions: form.groupSectionsWithQuestions || false,
                    label_font_size: form.labelFontSize || '14px'
                  };
                  localStorage.setItem('previewForm', JSON.stringify(formDataWithSettings));
                  console.log('FormBuilder: Saved preview data to localStorage:', formDataWithSettings);
                  console.log('FormBuilder: localStorage keys after save:', Object.keys(localStorage));
                  console.log('FormBuilder: Verifying saved data:', localStorage.getItem('previewForm')?.substring(0, 100) + '...');
                  window.open('/preview/preview', '_blank');
                }}>
                  <FiEye />
                  Preview Form
                </QuickActionButton>
                <QuickActionButton onClick={openShareModal}>
                  <FiShare2 />
                  Share Publicly
                </QuickActionButton>
                <QuickActionButton onClick={openUserShareModal}>
                  <FiUsers />
                  Share with Users
                </QuickActionButton>
              </div>
            </div>
          )}
        </FormPreview>
      </MainPanel>

      {showPreview && (
        <div style={{ marginTop: '24px', padding: '20px', border: '2px solid #e5e7eb', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
            User Preview - Test Mode
          </h2>
          <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '4px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#1f2937' }}>
              {form.title || 'Click to Add Form Title'}
            </div>
            {form.description && (
              <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '16px' }}>
                {form.description}
              </div>
            )}
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              Preview is in minimal test mode. No styled components used.
            </div>
          </div>
        </div>
      )}

      </MainContent>

      {/* Save Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            maxWidth: '500px',
            padding: '32px',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: 'black',
              margin: '0 0 24px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              💾 Save Form
            </h2>
            
            <p style={{
              color: '#6b7280',
              fontSize: '16px',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Choose where to save your form:
            </p>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Select Folder:
              </label>
              <select
                value={saveFolderId || ''}
                onChange={(e) => setSaveFolderId(e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: '2px solid rgba(0, 0, 0, 0.2)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  transition: 'all 0.3s ease'
                }}
              >
                <option value="">No folder (save in root)</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>
                    📁 {folder.name}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                You can move this form to a different folder later
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowSaveModal(false)}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '12px',
                  background: 'rgba(107, 114, 128, 0.1)',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSaveForm}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '12px',
                  background: 'black',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {saving ? 'Saving...' : (
                  <>
                    💾 Save Form
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        form={shareModal.form}
        routeFormId={formId}
      />

      <UserShareModal
        isOpen={userShareModal.isOpen}
        onClose={closeUserShareModal}
        item={userShareModal.item}
        type="form"
      />



      {showComputationExamples && <ComputationExamples />}


      {(() => {
        console.log('showFieldSelector state:', showFieldSelector);
        return showFieldSelector;
      })() && (
        <>
          <FieldSelectorOverlay onClick={closeFieldSelector} />
          <FieldSelectorModal position={fieldSelectorPosition}>
            <FieldSelectorHeader>
              <FieldSelectorTitle>Choose Field Type</FieldSelectorTitle>
              <FieldSelectorClose onClick={closeFieldSelector}>×</FieldSelectorClose>
            </FieldSelectorHeader>
            <FieldSelectorGrid>
              {(() => {
                const filteredTypes = fieldTypes.filter(fieldType => fieldType.type !== 'contact');
                console.log('Available field types in selector:', filteredTypes.map(ft => ({ type: ft.type, label: ft.label })));
                return filteredTypes.map((fieldType) => {
                  const IconComponent = fieldType.icon;
                  return (
                    <FieldSelectorOption
                      key={fieldType.type}
                      onClick={() => {
                        console.log('Selected field type:', fieldType.type);
                        selectFieldType(fieldType.type);
                      }}
                    >
                      <FieldSelectorIcon>
                        <IconComponent />
                      </FieldSelectorIcon>
                      <FieldSelectorLabel>{fieldType.label}</FieldSelectorLabel>
                      <FieldSelectorDescription>{fieldType.description}</FieldSelectorDescription>
                    </FieldSelectorOption>
                  );
                });
              })()}
            </FieldSelectorGrid>
          </FieldSelectorModal>
        </>
      )}

      {/* Change Field Type Modal */}
      {changeFieldTypeId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              Change Field Type
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '14px' }}>
              Select a new field type for this field. The field label and basic properties will be preserved.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              {[
                { type: 'text', label: 'Text', icon: '📝' },
                { type: 'email', label: 'Email', icon: '📧' },
                { type: 'number', label: 'Number', icon: '🔢' },
                { type: 'date', label: 'Date', icon: '📅' },
                { type: 'textarea', label: 'Textarea', icon: '📄' },
                { type: 'select', label: 'Select', icon: '📋' },
                { type: 'radio', label: 'Radio', icon: '🔘' },
                { type: 'checkbox', label: 'Checkbox', icon: '☑️' },
                { type: 'rating', label: 'Rating', icon: '⭐' },
                { type: 'slider', label: 'Slider', icon: '🎚️' },
                { type: 'mapLocation', label: 'Current location', icon: '📍' }
              ].map(fieldType => (
                <button
                  key={fieldType.type}
                  onClick={() => changeFieldType(changeFieldTypeId, fieldType.type)}
                  style={{
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{fieldType.icon}</span>
                  <span>{fieldType.label}</span>
                </button>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setChangeFieldTypeId(null)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      </BuilderContainer>
    </>
  );
}

export default FormBuilder;
